import { TransitPanel, type TransitStop } from "./TransitPanel";
import { TransportPicker } from "./TransportPicker";
import { isCycling, usesRoadTraffic, transportLabels, type TravelMode } from "./lib/transport";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode, CSSProperties } from "react";
import { SheetHandle } from "./SheetHandle";
import { namedPlace, needsPlaceLabel } from "./lib/placeLabel";
import { allowedHebrewVoice } from "./lib/deviceVoice";
import { RouteOverview, ManeuverIcon } from "./RouteOverview";
import { createPortal } from "react-dom";
import { PermissionSetup } from "./PermissionSetup";
import OriginPicker from "./OriginPicker";
import AccountPanel from "./AccountPanel";
import HourlyTraffic from "./HourlyTraffic";
import { useAccountHistory, type NavigationHistoryEntry } from "./lib/account";
import { acquireCurrentPosition, useCurrentLocation } from "./lib/currentLocation";
import { useBackNavigation } from "./lib/backNavigation";
import { LaneGuidance } from "./LaneGuidance";
import { Speedometer } from "./Speedometer";
import {
  Navigation,
  Search,
  Home,
  BriefcaseBusiness,
  Star,
  Map,
  Settings2,
  Bell,
  ArrowUpRight,
  ArrowLeft,
  Plus,
  Minus,
  LocateFixed,
  SlidersHorizontal,
  X,
  ShieldCheck,
  Camera,
  TriangleAlert,
  Car,
  Construction,
  TrafficCone,
  ChevronLeft,
  Clock3,
  MapPin,
  Volume2,
  VolumeX,
  Check,
  LoaderCircle,
  Trash2,
  Play,
  CircleHelp,
  Compass,
  Route as RouteIcon,
  Flag,
  RefreshCw,
  Pencil,
  Sun,
  Moon,
  Coffee,
  Trees,
  ParkingCircle,
  Mic,
  Square,
  Radio,
  Fuel,
  Sparkles,
  CameraOff,
  Video,
  Droplets,
  Package,
  Download,
  UserRound,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import MapView from "./MapView";
import Discovery from "./Discovery";
import PlaceSuggestion from "./PlaceSuggestion";
import CameraCatalogPanel from "./CameraCatalogPanel";
import { cameraLabel, nextCameraAlert } from "./lib/cameraAlerts";
import {
  SpeechArbiter,
  isSpeechPlaybackPending,
  type SpeechLease,
} from "./lib/speechArbiter";
import {
  isGooglePlace,
  isLocationInput,
  normalizedLocationInput,
} from "./lib/search";
import { carContext, carNavigation, type CarNavigationState } from "./lib/car";
import { useInstalledApp } from "./lib/installedApp";
import { useVisualViewport } from "./lib/visualViewport";
import type { MapHandle } from "./MapView";
import { api, ApiError } from "./lib/api";
import {
  freshFix,
  locationFix,
  useCommunity,
  useTraffic,
} from "./lib/community";
import { f5Speech, voiceConfig } from "./lib/speech";
import type { VoiceConfig } from "./lib/speech";
import { useVoiceRecorder } from "./lib/voiceRecorder";
import { useHandsFreeRecorder } from "./lib/handsFreeRecorder";
import { VoiceNavigationSession } from "./lib/voiceNavigationSession";
import { parseVoiceCommand } from "./lib/voiceCommands";
import {
  isCameraReport,
  isDangerReport,
  observationCandidates,
  observationTarget,
} from "./lib/reports";
import type {
  LatLng,
  Place,
  RoadReport,
  ReportType,
  Route,
  SavedPlace,
  LocationFix,
  NearbyPlace,
  PlaceCategory,
  CameraCatalog,
  CameraSite,
} from "./lib/types";
import {
  getSavedPlaces,
  savePlace,
  removePlace,
  getRecents,
  addRecent,
  clearRecents,
  getSettings,
  saveSettings,
} from "./lib/storage";
import {
  computeProgress,
  distanceMeters,
  formatDistance,
  formatDuration,
  guidanceInstruction,
} from "./lib/navigation";

declare global {
  interface Window {
    DerechLocation?: Geolocation;
    DerechSpeech?: {
      supportsCallbacks?: boolean;
      speak: (
        text: string,
        callbacks?: { onEnd?: () => void; onError?: (error: unknown) => void },
      ) => unknown;
      stop: () => void;
    };
  }
}
const START: LatLng = { lat: 32.0808, lng: 34.7805 };
const NO_CAMERA_ALERTS: ReadonlySet<string> = new Set();
const PICKS: Place[] = [
  {
    id: "tlv-port",
    name: "נמל תל אביב",
    address: "תל אביב–יפו",
    lat: 32.0971,
    lng: 34.7742,
  },
  {
    id: "hayarkon",
    name: "פארק הירקון",
    address: "שדרות רוקח, תל אביב–יפו",
    lat: 32.1004,
    lng: 34.8117,
  },
  {
    id: "dizengoff",
    name: "כיכר דיזנגוף",
    address: "תל אביב–יפו",
    lat: 32.0775,
    lng: 34.7735,
  },
];
const REPORTS: Record<
  ReportType,
  { label: string; icon: LucideIcon; color: string; detail: string }
> = {
  POLICE: {
    label: "משטרה",
    icon: ShieldCheck,
    color: "blue",
    detail: "נוכחות משטרה בדרך",
  },
  CAMERA: {
    label: "מצלמה",
    icon: Camera,
    color: "purple",
    detail: "מצלמת מהירות או רמזור",
  },
  FIXED_CAMERA: {
    label: "מצלמת דרך קבועה",
    icon: Camera,
    color: "purple",
    detail: "מצלמה קבועה לצד הדרך, למהירות או לרמזור",
  },
  MOBILE_CAMERA: {
    label: "מצלמת דרך ניידת",
    icon: Video,
    color: "blue",
    detail: "מצלמה מתניידת או עמדת אכיפה זמנית",
  },
  SLIPPERY_ROAD: {
    label: "כביש מחליק",
    icon: Droplets,
    color: "blue",
    detail: "מקטע כביש חלק או מחליק",
  },
  OBJECT_ON_ROAD: {
    label: "עצם על הכביש",
    icon: Package,
    color: "orange",
    detail: "חפץ או עצם שמפריע לתנועה על הכביש",
  },
  HAZARD: {
    label: "סכנה בדרך",
    icon: TriangleAlert,
    color: "orange",
    detail: "מכשול או מפגע בכביש",
  },
  ACCIDENT: { label: "תאונה", icon: Car, color: "red", detail: "תאונה באזור" },
  TRAFFIC: {
    label: "עומס תנועה",
    icon: TrafficCone,
    color: "orange",
    detail: "תנועה איטית או עומדת",
  },
  CLOSURE: {
    label: "חסימת כביש",
    icon: Construction,
    color: "red",
    detail: "דרך חסומה או עבודות",
  },
};
const reportTypes = Object.keys(REPORTS) as ReportType[];
const newReportTypes = reportTypes.filter((type) => type !== "CAMERA");
type Section = "explore" | "saved" | "reports" | "settings";
type Mode = "idle" | "live" | "simulation";
interface VoiceCaptureContext {
  searchOnly?: boolean;
  question: RoadReport | null;
  fix: LocationFix | null;
}
function Modal({
  children,
  onClose,
  label,
}: {
  children: ReactNode;
  onClose: () => void;
  label: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    ref.current?.showModal();
  }, []);
  return (
    <dialog
      ref={ref}
      className="modal"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      aria-label={label}
    >
      <header className="modal-toolbar">
        <span>{label}</span>
        <button
          className="icon-btn modal-close"
          onClick={onClose}
          aria-label="סגירה"
        >
          <X size={20} />
        </button>
      </header>
      <div className="modal-inner">
        {children}
      </div>
    </dialog>
  );
}
function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      className={"toggle " + (checked ? "on" : "")}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
    >
      <span />
    </button>
  );
}
function App() {
  useVisualViewport();
  const installedApp = useInstalledApp();
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine !== false);
    window.addEventListener("online", update); window.addEventListener("offline", update);
    return () => { window.removeEventListener("online", update); window.removeEventListener("offline", update); };
  }, []);
  const account = useAccountHistory();
  const [accountPanel, setAccountPanel] = useState(false);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("account") === "1") setAccountPanel(true);
  }, []);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [sheetFull, setSheetFull] = useState(false);
  const [sheetDrag, setSheetDrag] = useState<number | null>(null);
  const [transportOpen, setTransportOpen] = useState(false);
  const [transitStops, setTransitStops] = useState<TransitStop[]>([]);
  const [routeOverview, setRouteOverview] = useState(false);
  const [hourlyTraffic, setHourlyTraffic] = useState(false);
  const [tripId, setTripId] = useState<string | null>(null);
  const tripRef = useRef<string | null>(null);
  const [carDriving, setCarDriving] = useState(false);
  const carDrivingRef = useRef(false);
  const [section, setSection] = useState<Section>("explore"),
    [query, setQuery] = useState(() => (new URLSearchParams(window.location.search).get("destination") || "").slice(0, 240)),
    [results, setResults] = useState<Place[]>([]),
    [searching, setSearching] = useState(false),
    [searchError, setSearchError] = useState("");
  const [saved, setSaved] = useState(getSavedPlaces),
    [guestRecents, setRecents] = useState(getRecents),
    [settings, setSettings] = useState(getSettings),
    [dark, setDark] = useState(false);
  const selectionLabelAbort = useRef<AbortController | null>(null);
  useEffect(() => () => selectionLabelAbort.current?.abort(), []);
  // Account destinations never enter device-wide recent-search storage.
  const recents = !account.ready ? [] : account.user
    ? account.entries.map(entry => namedPlace(entry.destination)).filter((place): place is Place => place !== null)
        .filter((place, index, all) => all.findIndex(other => other.id === place.id) === index)
    : guestRecents;
  function rememberRecent(place: Place) {
    if (account.ready && !account.user) setRecents(addRecent(place));
  }
  async function clearOwnHistory() {
    const guest = !account.user;
    const cleared = await account.clearHistory();
    if (cleared && guest) { clearRecents(); setRecents([]); }
    return cleared;
  }
  function planHistoryDestination(entry: NavigationHistoryEntry) {
    setAccountPanel(false);
    setSaveKind(null);
    // A previous journey is a destination shortcut; refresh the current origin.
    manualOrigin.current = false;
    setOriginManual(false);
    setOriginName("");
    void selectPlace(entry.destination, true);
  }
  const [origin, setOrigin] = useState<LatLng>(START),
    [originManual, setOriginManual] = useState(false),
    [position, setPosition] = useState<LatLng | null>(null),
    [simPosition, setSimPosition] = useState<LatLng | null>(null),
    [destination, setDestination] = useState<Place | null>(null),
    [routes, setRoutes] = useState<Route[]>([]),
    [routeIndex, setRouteIndex] = useState(0),
    [routeBusy, setRouteBusy] = useState(false),
    [mode, setMode] = useState<Mode>("idle"),
    [gpsBusy, setGpsBusy] = useState(false),
    [gpsIssue, setGpsIssue] = useState("");
  const [originName, setOriginName] = useState("");
  const [originPicker, setOriginPicker] = useState(false);
  const [originError, setOriginError] = useState("");
  // Update synchronously when a choice is made so a late GPS callback cannot replace it.
  const manualOrigin = useRef(false);
  const [filters, setFilters] = useState<ReportType[]>(reportTypes),
    [showFilters, setShowFilters] = useState(false),
    [reportModal, setReportModal] = useState(false),
    [reportType, setReportType] = useState<ReportType | null>(null),
    [reportPosition, setReportPosition] = useState<LatLng>(START),
    [reportNote, setReportNote] = useState(""),
    [reportBusy, setReportBusy] = useState(false),
    [activeReport, setActiveReport] = useState<RoadReport | null>(null);
  const [cameraRemoval, setCameraRemoval] = useState(false);
  const [cameraCatalog, setCameraCatalog] = useState<CameraCatalog | null>(
    null,
  );
  const [catalogError, setCatalogError] = useState("");
  const [catalogVersion, setCatalogVersion] = useState(0);
  const [showCatalog, setShowCatalog] = useState(false);
  const [showCameraLayer, setShowCameraLayer] = useState(true);
  const [activeCamera, setActiveCamera] = useState<CameraSite | null>(null);
  const [cameraClock, setCameraClock] = useState(Date.now);
  const seenCameraAlerts = useRef(new Set<string>());
  const [cameraCandidates, setCameraCandidates] = useState<RoadReport[]>([]);
  const [cameraRemovalOrigin, setCameraRemovalOrigin] = useState<LatLng | null>(
    null,
  );
  const [cameraRemovalError, setCameraRemovalError] = useState("");
  const cameraLookup = useRef<AbortController | null>(null);
  useEffect(() => () => cameraLookup.current?.abort(), []);
  const [saveKind, setSaveKind] = useState<SavedPlace["kind"] | null>(null),
    [pickMode, setPickMode] = useState<"destination" | "origin" | "report" | "place">(
      "destination",
    ),
    [toast, setToast] = useState(""),
    [about, setAbout] = useState(false),
    [tileUrl, setTileUrl] = useState(
      "/maps/20261003/{z}/{x}/{y}.png",
    ),
    [tileError, setTileError] = useState(false),
    [reportVersion, setReportVersion] = useState(0);
  const [fix, setFix] = useState<LocationFix | null>(null),
    [safetyNotice, setSafetyNotice] = useState("");
  const [confirmationReport, setConfirmationReport] =
    useState<RoadReport | null>(null);
  const [speechPlaying, setSpeechPlaying] = useState(false);
  const [speechStatus, setSpeechStatus] = useState(""),
    [voiceServices, setVoiceServices] = useState<VoiceConfig | null>(null);
  const [speechVersion, setSpeechVersion] = useState(0);
  const [speechArbiter] = useState(
    () => new SpeechArbiter(() => setSpeechVersion((v) => v + 1)),
  );
  const speechTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [voicePanel, setVoicePanel] = useState(false);
  const [voiceHelp, setVoiceHelp] = useState(false);
  const [voiceMessage, setVoiceMessage] = useState("");
  const [voiceDestination, setVoiceDestination] = useState<Place | null>(null);
  const [voiceNavigation] = useState(() => new VoiceNavigationSession());
  const [discovery, setDiscovery] = useState<{
    category: PlaceCategory;
    center: LatLng;
    centerLabel: string;
  } | null>(null);
  const [suggestionPoint, setSuggestionPoint] = useState<LatLng | null>(null);
  function suggestPlace() {
    setSuggestionPoint(null); setDiscovery(null); setPickMode("place");
    setSection("explore"); setSheetExpanded(false); setSheetFull(false);
    tell("לחצו במפה בדיוק על המקום שתרצו להציע");
  }
  const [nearbyPlaces, setNearbyPlaces] = useState<NearbyPlace[]>([]);
  useEffect(() => {
    const sync = () =>
      window.DerechCar?.updateContext(carContext(saved, usesRoadTraffic(settings.travelMode) ? destination : null, settings));
    sync();
    window.addEventListener("derech:car-ready", sync);
    return () => window.removeEventListener("derech:car-ready", sync);
  }, [saved, destination, settings]);
  function discover(category: PlaceCategory) {
    // Discovery buttons explicitly plan a route, even after entering the save-place flow.
    setSaveKind(null);
    setPickMode("destination");
    setDiscovery({
      category,
      centerLabel: mode === "simulation" ? "סביב נקודת ההדמיה" : freshFix(fix) && position ? "סביב המיקום הנוכחי" : originManual ? "סביב נקודת המוצא שבחרתם" : "סביב מרכז המפה — מיקום נוכחי לא זמין",
      center:
        mode === "simulation"
          ? simPosition || origin
          : (freshFix(fix) ? position : null) ||
            (originManual ? origin : mapHandle.current?.center() || origin),
    });
  }
  function selectDiscovery(place: Place) {
    setDiscovery(null);
    setNearbyPlaces([]);
    const travelMode = "category" in place && place.category === "shelter" ? "walking" : settings.travelMode;
    if (travelMode !== settings.travelMode) updateSettings({ travelMode });
    void selectPlace(place, false, travelMode);
  }
  const askedReports = useRef(new Set<string>()),
    voicePosted = useRef({ type: "", at: 0 });
  const latestFix = useRef(fix);
  latestFix.current = fix;
  const liveContext = useRef({ mode, confirmationReport });
  liveContext.current = { mode, confirmationReport };
  const {
    reports,
    setReports,
    state: communityState,
    error: reportError,
  } = useCommunity(
    mode === "simulation" ? origin : position || origin,
    reportVersion,
  );
  const mapHandle = useRef<MapHandle | null>(null),
    searchInput = useRef<HTMLInputElement>(null),
    sidebarRef = useRef<HTMLElement>(null),
    routeAbort = useRef<AbortController | null>(null),
    watch = useRef<{ id: number; geo: Geolocation } | null>(null),
    progressRef = useRef(0),
    rerouteAt = useRef(0),
    lastSpoken = useRef(""),
    seenAlerts = useRef(new Set<string>());
  const route = routes[routeIndex] || null;
  const progressRoute = useRef<Route | null>(null),
    progressMode = useRef<Mode>("idle"),
    navigationEpoch = useRef(0),
    latestLocation = useRef<LatLng>(origin);
  latestLocation.current = mode === "live" && position ? position : origin;
  const originLabel = originManual
    ? originName || "נקודה שנבחרה במפה"
    : position ? "המיקום הנוכחי שלי" : "בחירת נקודת מוצא";
  function applyPosition(p: GeolocationPosition, refreshRoute = true) {
    const next = { lat: p.coords.latitude, lng: p.coords.longitude };
    setFix(locationFix(p));
    setPosition(next);
    if (!manualOrigin.current && mode === "idle") {
      if (!destination || refreshRoute) setOrigin(next);
      if (!destination && !position) mapHandle.current?.locate(next);
      else if (destination && refreshRoute) void requestRoutes(next, destination);
    }
  }
  const applyPositionRef = useRef(applyPosition);
  applyPositionRef.current = applyPosition;
  useCurrentLocation({ enabled: mode === "idle" && !gpsBusy, onPosition: applyPosition });
  if (progressRoute.current !== route || progressMode.current !== mode) {
    progressRef.current = 0;
    lastSpoken.current = "";
    progressRoute.current = route;
    progressMode.current = mode;
  }
  const displayPosition = mode === "simulation" ? simPosition : position;
  const progress =
    route && displayPosition && mode !== "idle"
      ? computeProgress(
          route,
          displayPosition,
          mode === "simulation" ? undefined : progressRef.current,
        )
      : null;
  const traffic = useTraffic(
    usesRoadTraffic(settings.travelMode) ? route : null,
    mode === "live" && !gpsIssue,
    fix,
    settings.shareTraffic,
    !!progress?.arrived,
  );
  const recorder = useVoiceRecorder<VoiceCaptureContext>(async (text, signal, context) => {
    if (context.searchOnly) {
      signal.throwIfAborted();
      setSection("explore"); setQuery(text); setSheetExpanded(true); setVoicePanel(false);
      return;
    }
    await handleVoiceTranscript(text, signal, context);
  });
  const voiceInputBusy = useRef(false);
  voiceNavigation.dependencies = {
    search: async (query: string, signal: AbortSignal) => {
      const homeOrWork = /^(?:בית|הביתה|הבית)$/.test(query)
        ? "home"
        : /^(?:עבודה|העבודה)$/.test(query)
          ? "work"
          : null;
      const stored = homeOrWork
        ? saved.filter((p) => p.kind === homeOrWork)
        : [];
      if (homeOrWork && !stored.length)
        throw new Error("המקום הזה עדיין לא שמור. אמרו כתובת מלאה.");
      return homeOrWork
        ? stored
        : api.search(query, position || origin, signal);
    },
    navigate: async (place: Place, signal: AbortSignal) => {
      signal.throwIfAborted();
      stopSpeech();
      setSaveKind(null);
      setQuery("");
      setSection("explore");
      routeAbort.current?.abort();
      if (watch.current) {
        watch.current.geo.clearWatch(watch.current.id);
        watch.current = null;
      }
      setMode("idle");
      setRoutes([]);
      setRouteBusy(false);
      setDestination(place);
      rememberRecent(place);
      await startNavigation(place, signal);
    },
    announce: (message, lifecycle) => {
      setVoiceMessage(message);
      speakOwned(message, "community", () => !document.hidden && lifecycle.isCurrent(), undefined, lifecycle.onComplete);
      const lease = speechArbiter.current;
      return () => { if (lease && speechArbiter.owns(lease)) stopSpeech(); };
    },
    changed: (view) => {
      setVoiceMessage(view.message);
      setVoiceDestination(view.choices[view.selectedIndex] || null);
    },
  };
  const handsFree = useHandsFreeRecorder({
    enabled: mode !== "simulation" && !carDriving,
    paused:
      (settings.speechProvider === "f5"
        ? speechPlaying
        : !!speechArbiter.current) || recorder.state !== "idle",
    getContext: () => ({
      question: liveContext.current.confirmationReport || activeReport,
      fix: latestFix.current ? { ...latestFix.current } : null,
    }),
    onTranscript: handleVoiceTranscript,
  });
  useEffect(() => {
    // Navigation already owns a GPS watch. Idle voice reports also need a fresh
    // position at voice onset, even minutes after enabling the microphone.
    if (!handsFree.active || handsFree.state === "requesting" || mode !== "idle" || document.hidden) return;
    const geo = window.DerechLocation ?? navigator.geolocation;
    if (!geo) return;
    let stopped = false;
    const id = geo.watchPosition(
      (position) => {
        if (stopped || document.hidden) return;
        const value = locationFix(position);
        latestFix.current = value;
        setFix(value);
        if (freshFix(value)) applyPositionRef.current(position, false);
      },
      () => {
        if (stopped) return;
        latestFix.current = null;
        setFix(null);
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 },
    );
    const stop = () => {
      if (stopped) return;
      stopped = true;
      geo.clearWatch(id);
    };
    const onVisibility = () => { if (document.hidden) stop(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("derech:media-stopped", stop);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("derech:media-stopped", stop);
    };
  }, [handsFree.active, handsFree.state === "requesting", mode]);
  const voiceBusy =
    recorder.state !== "idle" ||
    handsFree.state === "recording" ||
    handsFree.state === "transcribing";
  voiceInputBusy.current = voiceBusy;
  const cameraAlert =
    route &&
    progress &&
    displayPosition &&
    mode !== "idle" &&
    settings.reportAlerts &&
    !gpsIssue &&
    (mode === "simulation" ||
      (freshFix(fix) && cameraClock - fix.observedAt <= 30_000))
      ? nextCameraAlert(
          cameraCatalog?.cameras || [],
          route,
          displayPosition,
          progress,
          NO_CAMERA_ALERTS,
        )
      : null;
  const cameraAlertContext = useRef({
    id: cameraAlert?.camera.id,
    enabled: settings.voiceAlerts,
    recording: voiceBusy,
  });
  cameraAlertContext.current = {
    id: cameraAlert?.camera.id,
    enabled: settings.voiceAlerts,
    recording: voiceBusy,
  };
  useEffect(() => {
    seenCameraAlerts.current.clear();
  }, [mode]);
  useEffect(() => {
    if (mode !== "live") return;
    setCameraClock(Date.now());
    const timer = setInterval(() => setCameraClock(Date.now()), 5000);
    return () => clearInterval(timer);
  }, [mode]);
  useEffect(() => {
    const controller = new AbortController();
    setCatalogError("");
    api
      .cameras(controller.signal)
      .then(setCameraCatalog)
      .catch(() => {
        if (!controller.signal.aborted)
          setCatalogError(
            "לא ניתן לטעון את מאגר המצלמות. דיווחי הקהילה ממשיכים לפעול.",
          );
      });
    return () => controller.abort();
  }, [catalogVersion]);
  useEffect(() => {
    if (progress && !progress.offRoute)
      progressRef.current = Math.max(progressRef.current, progress.progress);
  }, [progress]);
  useEffect(() => {
    api
      .config()
      .then((c) => setTileUrl(c.tileUrl))
      .catch(() => {});
    voiceConfig()
      .then(setVoiceServices)
      .catch(() => setVoiceServices(null));
  }, []);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 5200);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    const controller = new AbortController();
    if (query.trim().length < 2) {
      setResults([]);
      setSearching(false);
      setSearchError("");
      return;
    }
    setSearching(true);
    setSearchError("");
    const timer = setTimeout(() => {
      const pending = isLocationInput(query)
        ? api
            .resolveLocation(normalizedLocationInput(query), controller.signal)
            .then((p) => [p])
        : api.search(query, position ?? origin, controller.signal);
      pending
        .then((places) => {
          if (!controller.signal.aborted) setResults(places);
        })
        .catch((e) => {
          if (!controller.signal.aborted) {
            setResults([]);
            setSearchError(e.message);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setSearching(false);
        });
    }, 550);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, origin]);
  useEffect(
    () => () => {
      if (watch.current) watch.current.geo.clearWatch(watch.current.id);
      routeAbort.current?.abort();
      f5Speech.stop();
      if (speechTimer.current) clearTimeout(speechTimer.current);
    },
    [],
  );
  function tell(message: string) {
    setToast(message);
  }
  function stopSpeech(preserveText?: string) {
    speechArbiter.clear();
    if (speechTimer.current) clearTimeout(speechTimer.current);
    speechTimer.current = null;
    f5Speech.stop(preserveText);
    setSpeechPlaying(false);
    setSpeechStatus("");
    if (window.DerechSpeech) window.DerechSpeech.stop();
    else if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  }
  function speakOwned(
    text: string,
    kind: SpeechLease["kind"],
    isCurrent: () => boolean,
    cameraId?: string,
    onComplete?: (played: boolean) => void,
    preview = false,
    diagnostic?: "device" | "server",
  ): boolean {
    if (carDrivingRef.current) { setSpeechStatus("בדיקת שמע בזמן חיבור לרכב זמינה במסך הרכב"); onComplete?.(false); return false; }
    if (kind !== "camera") stopSpeech(text);
    const lease = speechArbiter.begin(kind, cameraId);
    if (!lease) { onComplete?.(false); return false; }
    const finish = (played = false) => {
      if (!speechArbiter.finish(lease)) return;
      if (speechTimer.current) clearTimeout(speechTimer.current);
      speechTimer.current = null;
      setSpeechPlaying(false);
      onComplete?.(played && isCurrent());
    };
    let deviceFallbackStarted = false;
    const playDevice = (fallback = false) => {
      if (!speechArbiter.owns(lease) || !isCurrent() || voiceInputBusy.current) { finish(); return; }
      if (speechTimer.current) clearTimeout(speechTimer.current);
      if (fallback) f5Speech.stop();
      setSpeechPlaying(true);
      setSpeechStatus(fallback ? "שירות הדיבור אינו זמין · מקריאים בקול המכשיר" : "מקריאים בקול המכשיר");
      const done = (played: boolean) => {
        if (!speechArbiter.owns(lease)) return;
        if (deviceFallbackStarted) return;
        if (!played && !fallback && !diagnostic && isCurrent() && !voiceInputBusy.current) {
          deviceFallbackStarted = true;
          if (speechTimer.current) clearTimeout(speechTimer.current);
          speechTimer.current = null;
          setSpeechStatus("קול המכשיר אינו זמין · עוברים לקול משופר");
          void f5Speech.speak(text, () => speechArbiter.owns(lease) && isCurrent() && !voiceInputBusy.current,
            event => {
              if (!speechArbiter.owns(lease)) return;
              setSpeechStatus(event.state === "playing" ? "מקריאים בקול משופר" : event.state === "error" ? event.message : "");
              if (!isSpeechPlaybackPending(event)) finish(event.state === "ended");
            });
          return;
        }
        setSpeechStatus(played ? (diagnostic ? "בדיקת השמע הסתיימה" : "") : "קול המכשיר אינו זמין. בדקו בהגדרות המכשיר שקול בעברית מותקן ונסו שוב");
        finish(played);
      };
      // New native hosts and browsers release on actual completion. Older APKs
      // still get a bounded fallback, which stops playback before rearming mic.
      speechTimer.current = setTimeout(
        () => {
          if (!speechArbiter.owns(lease)) return;
          if (window.DerechSpeech) window.DerechSpeech.stop();
          else if ("speechSynthesis" in window) window.speechSynthesis.cancel();
          done(false);
        },
        window.DerechSpeech?.supportsCallbacks
          ? 60_000
          : Math.min(30_000, Math.max(8000, text.length * 150)),
      );
      if (window.DerechSpeech)
        window.DerechSpeech.speak(text, { onEnd: () => done(true), onError: () => done(false) });
      else if ("speechSynthesis" in window) {
        const voice = allowedHebrewVoice(window.speechSynthesis.getVoices());
        if (!voice) { done(false); return; }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.voice = voice;
        utterance.lang = "he-IL";
        utterance.rate = 0.92;
        utterance.onend = () => done(true);
        utterance.onerror = () => done(false);
        window.speechSynthesis.speak(utterance);
      } else done(false);
    };
    if (diagnostic === "server" || (!diagnostic && settings.speechProvider === "f5")) {
      let played = false;
      // A preview can wait for natural speech; real driving guidance must remain timely.
      if (!diagnostic) speechTimer.current = setTimeout(() => playDevice(true), preview || liveContext.current.mode === "simulation" ? 60_000 : 8000);
      void f5Speech.speak(text, () => speechArbiter.owns(lease) && isCurrent() && !voiceInputBusy.current,
        event => {
          if (!speechArbiter.owns(lease)) return;
          if (event.state === "playing") {
            setSpeechPlaying(true);
            played = true;
            if (speechTimer.current) clearTimeout(speechTimer.current);
            speechTimer.current = null;
          }
          setSpeechStatus(event.state === "preparing" ? "מכינים הקראה…" : event.state === "playing" ? "מקריאים בקול משופר" : event.state === "error" ? event.message : "");
          if (!isSpeechPlaybackPending(event)) {
            if (event.state === "error" && !played && !diagnostic) playDevice(true);
            else {
              if (diagnostic && event.state === "ended") setSpeechStatus("בדיקת השמע הסתיימה");
              finish(played && event.state === "ended");
            }
          }
        });
    } else {
      playDevice();
    }
    return true;
  }
  useEffect(() => {
    const hide = () => {
      if (document.hidden) {
        stopSpeech();
        voiceNavigation.reset();
      }
    };
    const stopped = () => {
      stopSpeech();
      voiceNavigation.reset();
    };
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("derech:media-stopped", stopped);
    return () => {
      document.removeEventListener("visibilitychange", hide);
      window.removeEventListener("derech:media-stopped", stopped);
    };
  }, []);
  function updateSettings(change: Partial<typeof settings>) {
    f5Speech.unlock();
    if (change.voiceAlerts === true || change.speechProvider)
      lastSpoken.current = "";
    setSettings(saveSettings(change));
    if (change.voiceAlerts === false) stopSpeech();
  }
  function changeTransport(travelMode: TravelMode) {
    if (travelMode === settings.travelMode) return;
    stopNavigation();
    setRoutes([]); setRouteIndex(0); setSafetyNotice(""); setHourlyTraffic(false); setTransitStops([]);
    updateSettings({ travelMode });
    setTransportOpen(false);
    if (travelMode === "bus") setSheetExpanded(true);
    if (destination && travelMode !== "bus") void requestRoutes(mode === "live" && position ? position : origin, destination, travelMode);
  }
  function stopNavigation() {
    if (tripRef.current) window.DerechCar?.updateNavigation?.({ version: 1, tripId: tripRef.current, state: "stopped" });
    tripRef.current = null; setTripId(null);
    carDrivingRef.current = false; setCarDriving(false);
    navigationEpoch.current++;
    routeAbort.current?.abort();
    setRouteBusy(false);
    if (mode === "live" && position) {
      setOrigin(position);
      setOriginManual(false);
      manualOrigin.current = false;
    }
    setMode("idle");
    setSimPosition(null);
    setGpsBusy(false);
    if (watch.current) {
      watch.current.geo.clearWatch(watch.current.id);
      watch.current = null;
    }
    stopSpeech();
    setGpsIssue("");
    setConfirmationReport(null);
    recorder.cancel();
  }
  function finishNavigation() {
    stopNavigation();
    setDestination(null);
    setRoutes([]);
  }
  function startSimulation() {
    if (!route?.geometry.length) return;
    stopNavigation();
    // This must run within the click gesture, before asynchronous speech preparation.
    f5Speech.unlock();
    handsFree.stop();
    voiceNavigation.reset();
    lastSpoken.current = "";
    setSimPosition({ lat: route.geometry[0][1], lng: route.geometry[0][0] });
    setRouteOverview(false);
    setSheetExpanded(false);
    setSheetFull(false);
    setMode("simulation");
  }
  async function selectPlace(place: Place, fromCurrentLocation = false, travelMode: TravelMode = settings.travelMode) {
    selectionLabelAbort.current?.abort();
    const labelRequest = new AbortController();
    selectionLabelAbort.current = labelRequest;
    place = namedPlace(place) ?? place;
    if (needsPlaceLabel(place)) {
      const epoch = navigationEpoch.current;
      try {
        const label = await api.reverse(place, labelRequest.signal);
        place = { ...place, name: label.name, address: label.address, source: label.source };
      } catch { /* An unnamed point can still be selected, but is not a recent search. */ }
      if (labelRequest.signal.aborted || navigationEpoch.current !== epoch) return;
    }
    voiceNavigation.reset();
    if (saveKind && !fromCurrentLocation && !isGooglePlace(place)) {
      try {
        setSaved(savePlace({ ...place, kind: saveKind }));
        stopNavigation();
        setSaveKind(null);
        setQuery("");
        setSection("explore");
        setSheetExpanded(false);
        setDestination(null);
        setRoutes([]);
        routeAbort.current?.abort();
        tell("המקום נשמר");
      } catch (e) {
        tell((e as Error).message);
      }
      return;
    }
    let nextOrigin = !fromCurrentLocation && originManual ? origin : freshFix(fix) ? position : null;
    setSaveKind(null);
    stopNavigation();
    setDestination(place);
    setQuery("");
    setSection("explore");
    setRoutes([]);
    setRouteIndex(0);
    rememberRecent(place);
    // The map's initial center is not a real route origin. Acquire a fix or ask
    // for an address instead of silently planning from central Tel Aviv.
    const attempt = navigationEpoch.current;
    if (!nextOrigin) {
      setGpsBusy(true);
      try {
        const p = await getGps();
        if (attempt !== navigationEpoch.current) return;
        applyPosition(p, false);
        nextOrigin = { lat: p.coords.latitude, lng: p.coords.longitude };
      } catch {
        if (attempt === navigationEpoch.current) {
          setOriginError("לא התקבל מיקום מדויק. אפשר לבחור כתובת מוצא או נקודה במפה.");
          setOriginPicker(true);
        }
        return;
      } finally {
        if (attempt === navigationEpoch.current) setGpsBusy(false);
      }
    }
    setOrigin(nextOrigin);
    await requestRoutes(nextOrigin, place, travelMode);
  }
  async function requestRoutes(from: LatLng, to: Place, travelMode: TravelMode = settings.travelMode) {
    if (travelMode === "bus") { routeAbort.current?.abort(); setRoutes([]); setRouteBusy(false); return; }
    routeAbort.current?.abort();
    const controller = new AbortController();
    routeAbort.current = controller;
    setRouteBusy(true);
    try {
      const response = await api.routeDetails(
        from,
        to,
        settings.avoidDangers,
        controller.signal,
        travelMode,
      );
      if (!controller.signal.aborted) {
        setRoutes(response.routes);
        setSafetyNotice(response.safetyNotice || "");
        setRouteIndex(0);
      }
    } catch (e) {
      if (!controller.signal.aborted) {
        tell((e as Error).message);
        setSafetyNotice((e as Error).message);
        if (
          settings.avoidDangers &&
          e instanceof ApiError &&
          e.status === 422
        ) {
          stopNavigation();
          setRoutes([]);
        }
      }
    } finally {
      if (!controller.signal.aborted) setRouteBusy(false);
    }
  }
  const getGps = (signal?: AbortSignal) => {
    const geo = window.DerechLocation ?? navigator.geolocation;
    return geo ? acquireCurrentPosition(geo, { signal }) : Promise.reject(new Error("המכשיר אינו תומך במיקום"));
  };
  function openOriginPicker() {
    stopNavigation();
    setRouteOverview(false);
    setOriginError("");
    setOriginPicker(true);
  }
  function chooseOrigin(place: Place) {
    stopNavigation();
    manualOrigin.current = true;
    setOriginManual(true);
    setOriginName(place.name);
    setOrigin(place);
    setOriginPicker(false);
    setPickMode("destination");
    setOriginError("");
    mapHandle.current?.locate(place);
    if (destination) void requestRoutes(place, destination);
  }
  async function locate(useAsOrigin = false) {
    if (useAsOrigin) stopNavigation();
    const attempt = navigationEpoch.current;
    setGpsBusy(true);
    setOriginError("");
    try {
      const p = await getGps();
      if (attempt !== navigationEpoch.current) return;
      const next = { lat: p.coords.latitude, lng: p.coords.longitude };
      if (useAsOrigin) {
        manualOrigin.current = false;
        setOriginManual(false);
        setOriginName("");
        setOrigin(next);
        setOriginPicker(false);
      }
      applyPosition(p, false);
      mapHandle.current?.locate(next);
      if (destination && mode === "idle" && !manualOrigin.current) {
        setOrigin(next);
        await requestRoutes(next, destination);
      }
    } catch {
      if (attempt === navigationEpoch.current) {
        const message = "לא התקבל מיקום. אפשר לאפשר גישה למיקום בהגדרות המכשיר, או לבחור כתובת מוצא.";
        setOriginError(message);
        tell(message);
      }
    } finally {
      if (attempt === navigationEpoch.current) setGpsBusy(false);
    }
  }
  async function startNavigation(
    target: Place | null = destination,
    signal?: AbortSignal,
  ) {
    if (!target) return;
    if (settings.travelMode === "bus") { setDestination(target); setSection("explore"); setSheetExpanded(true); return; }
    signal?.throwIfAborted();
    routeAbort.current?.abort();
    setRouteBusy(false);
    f5Speech.unlock();
    askedReports.current.clear();
    const attempt = ++navigationEpoch.current;
    let stage: "location" | "route" = "location";
    setGpsBusy(true);
    try {
      const p = await getGps(signal);
      signal?.throwIfAborted();
      if (attempt !== navigationEpoch.current) return;
      if (p.coords.accuracy > 100) throw new Error("דיוק המיקום נמוך");
      const next = { lat: p.coords.latitude, lng: p.coords.longitude };
      setFix(locationFix(p));
      setPosition(next);
      setOrigin(next);
      setOriginManual(false);
      manualOrigin.current = false;
      stage = "route";
      const rr = await api.routeDetails(
        next,
        target,
        settings.avoidDangers,
        signal,
        settings.travelMode,
      );
      signal?.throwIfAborted();
      if (attempt !== navigationEpoch.current) return;
      setRoutes(rr.routes);
      setSafetyNotice(rr.safetyNotice || "");
      setRouteIndex(0);
      setMode("live");
      const nextTripId = crypto.randomUUID();
      tripRef.current = nextTripId;
      setTripId(nextTripId);
      carDrivingRef.current = false; setCarDriving(false);
      const selectedRoute = rr.routes[0];
      if (selectedRoute) {
        void account.recordNavigation({
          id: crypto.randomUUID(), startedAt: Date.now(),
          destination: { id: target.id.slice(0, 200), name: target.name.slice(0, 200), address: target.address.slice(0, 400), lat: target.lat, lng: target.lng },
          origin: { ...next, label: "המיקום הנוכחי שלי" },
          distanceMeters: selectedRoute.distance, durationSeconds: selectedRoute.duration,
        }).then(saved => { if (!saved) tell("הניווט התחיל, אך לא נשמר בהיסטוריה. אפשר לבדוק בחשבון והיסטוריה."); });
      }
    } catch (e) {
      if (signal) throw e;
      if (attempt === navigationEpoch.current)
        tell(
          stage === "route"
            ? (e as Error).message
            : "כדי לנווט צריך מיקום מדויק והרשאת GPS. אפשר גם להפעיל סימולציה של המסלול.",
        );
    } finally {
      if (attempt === navigationEpoch.current) setGpsBusy(false);
    }
  }
  useEffect(() => {
    if (mode !== "live" || carDriving) return;
    const geo = window.DerechLocation ?? navigator.geolocation;
    if (!geo) return;
    const epoch = navigationEpoch.current;
    let cancelled = false;
    const id = geo.watchPosition(p => {
      if (cancelled || epoch !== navigationEpoch.current || carDrivingRef.current) return;
      if (p.coords.accuracy > 100) { setGpsIssue("דיוק מיקום נמוך — ממתינים לאות GPS"); return; }
      setGpsIssue(""); setFix(locationFix(p));
      setPosition({ lat: p.coords.latitude, lng: p.coords.longitude });
    }, () => {
      if (!cancelled && epoch === navigationEpoch.current) setGpsIssue("אות המיקום אבד — ממתינים לחיבור מחדש");
    }, { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 });
    watch.current = { id, geo };
    return () => { cancelled = true; geo.clearWatch(id); if (watch.current?.id === id) watch.current = null; };
  }, [mode, carDriving, tripId]);
  const carEventHandler = useRef((event: CustomEvent<CarNavigationState>) => {});
  const finishingCarTrip = useRef<string | null>(null);
  carEventHandler.current = event => {
    const state = event.detail;
    if (!state || typeof state.connected !== "boolean" || typeof state.active !== "boolean") return;
    if (!usesRoadTraffic(settings.travelMode)) return;
    const matches = !!tripRef.current && state.tripId === tripRef.current;
    if (matches && state.ended === true) {
      if (finishingCarTrip.current === state.tripId) return;
      if (state.arrived === true && state.fix && state.fix.mock !== true && freshFix(state.fix, Date.now(), 50)) {
        const finishedTrip = tripRef.current!;
        const finalFix = state.fix;
        finishingCarTrip.current = finishedTrip;
        latestFix.current = finalFix; setFix(finalFix);
        setPosition({ lat: finalFix.lat, lng: finalFix.lng }); setGpsIssue("");
        void traffic.flushArrival(finalFix).finally(() => {
          if (tripRef.current === finishedTrip) stopNavigation();
          if (finishingCarTrip.current === finishedTrip) finishingCarTrip.current = null;
        });
      } else stopNavigation();
      return;
    }
    if (state.connected && !state.active && !state.ended && tripRef.current && destination && route && mode === "live") {
      const payload = carNavigation(tripRef.current, destination, route, latestFix.current, progressRef.current);
      if (payload) window.DerechCar?.updateNavigation?.(payload);
    }
    if (state.tripId && !matches) return;
    const owns = matches && state.connected && state.active;
    if (owns && !carDrivingRef.current) {
      stopSpeech(); handsFree.stop(); recorder.cancel(); voiceNavigation.reset();
    }
    carDrivingRef.current = owns; setCarDriving(owns);
    if (owns && state.fix && freshFix(state.fix, Date.now(), 100)) {
      latestFix.current = state.fix; setFix(state.fix);
      setPosition({ lat: state.fix.lat, lng: state.fix.lng }); setGpsIssue("");
    }
    if (owns && state.route && state.route.id !== route?.id && state.route.geometry?.length > 1) {
      setRoutes([state.route]); setRouteIndex(0);
    }
  };
  useEffect(() => {
    const receive = (event: Event) => carEventHandler.current(event as CustomEvent<CarNavigationState>);
    window.addEventListener("desty-car-navigation", receive);
    window.DerechCar?.requestNavigationState?.();
    return () => window.removeEventListener("desty-car-navigation", receive);
  }, []);
  useEffect(() => {
    const sync = () => {
      if (!usesRoadTraffic(settings.travelMode) || mode !== "live" || !tripId || !destination || !route || !window.DerechCar?.updateNavigation) return;
      const payload = carNavigation(tripId, destination, route, latestFix.current, progressRef.current);
      if (payload) window.DerechCar.updateNavigation(payload);
      else tell("המסלול ארוך מדי להעברה לרכב. אפשר לבחור את היעד במסך הרכב.");
      window.DerechCar.requestNavigationState?.();
    };
    sync(); window.addEventListener("derech:car-ready", sync);
    return () => window.removeEventListener("derech:car-ready", sync);
  }, [mode, tripId, destination, route, settings.travelMode]);
  useEffect(() => {
    if (mode !== "simulation" || !route) return;
    const coords = route.geometry;
    if (!coords.length) return;
    let index = 0;
    setSimPosition({ lat: coords[0][1], lng: coords[0][0] });
    const timer = setInterval(() => {
      // Do not outrun/cancel an instruction while its audio is loading or playing.
      if (document.hidden || speechArbiter.current) return;
      index = Math.min(
        coords.length - 1,
        index + Math.max(1, Math.floor(coords.length / 90)),
      );
      setSimPosition({ lat: coords[index][1], lng: coords[index][0] });
      if (index === coords.length - 1) clearInterval(timer);
    }, 750);
    return () => clearInterval(timer);
  }, [mode, route]);
  useEffect(() => {
    if (
      mode !== "live" ||
      carDriving ||
      !progress?.offRoute ||
      !position ||
      !destination ||
      routeBusy ||
      Date.now() - rerouteAt.current < 15000
    )
      return;
    rerouteAt.current = Date.now();
    tell("מחשבים מסלול מחדש מהמיקום שלך");
    requestRoutes(position, destination);
  }, [position, mode, progress?.offRoute, carDriving]);
  useEffect(() => {
    if (
      !settings.voiceAlerts ||
      carDriving ||
      mode === "idle" ||
      !route ||
      !progress ||
      progress.offRoute ||
      gpsIssue ||
      voiceBusy
    )
      return;
    const distance = progress.distanceToStep;
    const phase =
      distance > 800
        ? "far"
        : distance > 250
          ? "approach"
          : distance > 50
            ? "near"
            : "now";
    const instruction = guidanceInstruction(
      route.steps[progress.stepIndex],
      progress.arrived,
    );
    const key = progress.arrived
      ? "arrived"
      : `${route.id}-${progress.stepIndex}-${settings.speechProvider === "f5" ? "instruction" : phase}`;
    if (!instruction || lastSpoken.current === key) return;
    lastSpoken.current = key;
    const spokenDistance =
      distance >= 1000
        ? `${(distance / 1000).toFixed(1)} קילומטרים`
        : `${Math.max(10, Math.round(distance / 10) * 10)} מטרים`;
    const text =
      progress.arrived || phase === "now"
        ? instruction
        : `בעוד ${spokenDistance}, ${instruction}`;
    speakOwned(
      settings.speechProvider === "f5" ? instruction : text,
      "guidance",
      () => lastSpoken.current === key && liveContext.current.mode !== "idle",
    );
  }, [
    carDriving,
    progress?.stepIndex,
    progress?.arrived,
    progress?.distanceToStep,
    mode,
    settings.voiceAlerts,
    route,
    gpsIssue,
    settings.speechProvider,
    voiceBusy,
  ]);
  useEffect(() => {
    if (carDriving || !route || !settings.voiceAlerts || settings.speechProvider !== "f5")
      return;
    const step =
      progress?.stepIndex ?? (route.steps[0]?.type === "depart" ? 1 : 0);
    f5Speech.prefetch(
      route.steps.slice(step, step + 2).map((s) => guidanceInstruction(s)),
    );
  }, [
    route,
    carDriving,
    progress?.stepIndex,
    settings.voiceAlerts,
    settings.speechProvider,
  ]);
  useEffect(() => {
    if (
      !cameraAlert ||
      !settings.voiceAlerts ||
      voiceBusy ||
      seenCameraAlerts.current.has(cameraAlert.camera.id)
    )
      return;
    const { camera, message } = cameraAlert;
    // Maneuver guidance takes priority immediately before a turn.
    if ((progress?.distanceToStep ?? Infinity) < 70) return;
    const started = speakOwned(
      message,
      "camera",
      () =>
        cameraAlertContext.current.id === camera.id &&
        cameraAlertContext.current.enabled &&
        !cameraAlertContext.current.recording &&
        (liveContext.current.mode === "simulation" ||
          (freshFix(latestFix.current) &&
            Date.now() - latestFix.current.observedAt <= 30_000)),
      camera.id,
    );
    if (started) seenCameraAlerts.current.add(camera.id);
  }, [
    cameraAlert?.camera.id,
    mode,
    settings.voiceAlerts,
    settings.speechProvider,
    voiceBusy,
    progress?.distanceToStep,
    speechVersion,
  ]);
  useEffect(() => {
    const lease = speechArbiter.current;
    if (!lease || lease.kind !== "camera") return;
    const cancelOwnCamera = () => {
      // A delayed camera timeout must never cancel newer maneuver guidance.
      if (speechArbiter.owns(lease)) stopSpeech();
    };
    if (
      cameraAlert?.camera.id !== lease.cameraId ||
      !settings.voiceAlerts ||
      voiceBusy ||
      mode === "idle"
    ) {
      cancelOwnCamera();
      return;
    }
    if (mode === "live") {
      const remaining = (fix?.observedAt ?? 0) + 30_000 - Date.now();
      if (remaining <= 0) {
        cancelOwnCamera();
        return;
      }
      const timer = setTimeout(cancelOwnCamera, remaining);
      return () => clearTimeout(timer);
    }
  }, [
    cameraAlert?.camera.id,
    settings.voiceAlerts,
    voiceBusy,
    mode,
    fix?.observedAt,
    speechVersion,
  ]);
  const dangerKey = reports
    .filter((r) => isDangerReport(r.type))
    .map((r) => r.id)
    .sort()
    .join(",");
  const previousDangers = useRef(dangerKey);
  useEffect(() => {
    // A stream update may arrive while OSRM is still returning the old route.
    // Keep that update pending until the in-flight route has settled.
    if (routeBusy) return;
    const changed = dangerKey !== previousDangers.current;
    previousDangers.current = dangerKey;
    if (
      !changed ||
      !settings.avoidDangers ||
      mode !== "live" ||
      !route ||
      !position ||
      !destination
    )
      return;
    const onRemainingRoute = reports.some(
      (r) =>
        isDangerReport(r.type) &&
        (() => {
          const p = computeProgress(route, r);
          const radius =
            r.type === "ACCIDENT" ? 100 : r.type === "CLOSURE" ? 80 : 60;
          return (
            p.distanceFromRoute <= radius &&
            p.progress >= (progress?.progress ?? 0)
          );
        })(),
    );
    if (onRemainingRoute) {
      tell("התקבל דיווח על מפגע במסלול — בודקים דרך חלופית");
      void requestRoutes(position, destination);
    }
  }, [dangerKey, settings.avoidDangers, mode, routeBusy, route?.id]);
  useEffect(() => {
    if (mode !== "live" || !position || !settings.reportAlerts) return;
    for (const r of reports) {
      if (
        (r.expiresAt === null || r.expiresAt > Date.now()) &&
        !seenAlerts.current.has(r.id) &&
        distanceMeters(position, r) < 400
      ) {
        seenAlerts.current.add(r.id);
        tell(`${REPORTS[r.type].label} בקרבתך • דיווח קהילה`);
      }
    }
  }, [position, reports, mode, settings.reportAlerts]);
  useEffect(() => {
    if (
      mode !== "live" ||
      !position ||
      !settings.reportAlerts ||
      voiceBusy ||
      voiceNavigation.view.query ||
      !freshFix(fix)
    )
      return;
    if (confirmationReport) {
      if (
        !reports.some((r) => r.id === confirmationReport.id) ||
        distanceMeters(position, confirmationReport) > 500
      )
        setConfirmationReport(null);
      return;
    }
    const nearby = reports
      .filter(
        (r) =>
          !r.mine &&
          !askedReports.current.has(r.id) &&
          distanceMeters(position, r) < 180,
      )
      .sort(
        (a, b) => distanceMeters(position, a) - distanceMeters(position, b),
      )[0];
    if (nearby) {
      askedReports.current.add(nearby.id);
      setConfirmationReport(nearby);
      if (settings.voiceAlerts)
        speakOwned(
          reportQuestion(nearby),
          "community",
          () => liveContext.current.confirmationReport?.id === nearby.id,
        );
    }
  }, [
    reports,
    position,
    mode,
    settings.reportAlerts,
    confirmationReport,
    fix,
    voiceBusy,
  ]);
  useEffect(() => {
    if (activeReport) {
      const latest = reports.find((r) => r.id === activeReport.id);
      if (!latest) setActiveReport(null);
      else if (latest !== activeReport) setActiveReport(latest);
    }
  }, [reports]);
  function reportQuestion(r: RoadReport) {
    if (isCameraReport(r.type)) return "האם המצלמה עדיין כאן?";
    if (r.type === "SLIPPERY_ROAD") return "האם הכביש עדיין מחליק כאן?";
    if (r.type === "OBJECT_ON_ROAD") return "האם העצם עדיין על הכביש?";
    return r.type === "POLICE"
      ? "האם עדיין יש משטרה כאן?"
      : `האם ${REPORTS[r.type].label} עדיין כאן?`;
  }
  async function currentObservation(): Promise<LocationFix> {
    if (freshFix(latestFix.current)) return latestFix.current;
    const value = locationFix(await getGps());
    if (!freshFix(value))
      throw new Error("צריך מיקום GPS עדכני ומדויק כדי לאמת דיווח");
    setFix(value);
    setPosition({ lat: value.lat, lng: value.lng });
    return value;
  }
  async function resolveReport(r: RoadReport, signal?: AbortSignal) {
    if (mode === "simulation") {
      tell("אימות דיווחים זמין רק במיקום אמיתי");
      return;
    }
    setReportBusy(true);
    try {
      const observation = await currentObservation();
      signal?.throwIfAborted();
      await api.resolve(r.id, observation, signal);
      setReports((prev) => prev.filter((x) => x.id !== r.id));
      setConfirmationReport(null);
      setActiveReport(null);
      tell("תודה! האירוע הוסר מהמפה של כולם");
    } catch (e) {
      tell((e as Error).message);
      throw e;
    } finally {
      setReportBusy(false);
    }
  }
  async function handleVoiceTranscript(
    text: string,
    signal: AbortSignal,
    context: VoiceCaptureContext,
  ) {
    try {
      await performVoiceTranscript(text, signal, context);
    } catch (error) {
      if (!signal.aborted && (error as Error).name !== "AbortError") {
        const message = (error as Error).message;
        setVoiceMessage(message);
        if (handsFree.active)
          speakOwned(message, "community", () => !document.hidden);
      }
      throw error;
    }
  }
  async function performVoiceTranscript(
    text: string,
    signal: AbortSignal,
    { question, fix: spokenFix }: VoiceCaptureContext,
  ) {
    signal.throwIfAborted();
    if (liveContext.current.mode === "simulation")
      throw new Error("דיווח קולי אינו זמין בסימולציה");
    if (await voiceNavigation.handle(text, signal)) return;
    signal.throwIfAborted();
    const command = parseVoiceCommand(text, !!question);
    if (command.kind === "unknown") {
      if (handsFree.active) return;
      throw new Error(
        "לא זוהתה פקודה ברורה. אמרו ״מצלמה״, ״משטרה״ או ״ניווט״ ואז כתובת ו״אנטר״.",
      );
    }
    if (command.kind === "present" || command.kind === "absent") {
      const here = await currentObservation();
      signal.throwIfAborted();
      const nearbyReports = await api.reports(here, signal);
      signal.throwIfAborted();
      const selection = observationTarget(
        nearbyReports,
        here,
        command.type,
        question,
      );
      if (selection.ambiguous)
        throw new Error(
          "יש כמה מצלמות קרובות. בחרו את המצלמה במפה או בתפריט ״מצלמה כבר לא נמצאת״.",
        );
      const target = selection.target;
      if (!target)
        throw new Error("אין דיווח קרוב מתאים לאימות. בחרו דיווח במפה.");
      if (command.kind === "absent") await resolveReport(target, signal);
      else if (!(await confirmReport(target, signal)))
        throw new Error("אימות הדיווח לא הושלם");
      return;
    }
    if (
      voicePosted.current.type === command.type &&
      Date.now() - voicePosted.current.at < 30_000
    )
      throw new Error("הדיווח הזה כבר נשלח לפני רגע");
    const here = await currentObservation();
    signal.throwIfAborted();
    const reportFix = spokenFix ?? (!handsFree.active ? here : null);
    if (!freshFix(reportFix) || distanceMeters(reportFix, here) > 150)
      throw new Error(
        "לא היה מיקום GPS עדכני בזמן הדיבור או שהתרחקתם. הדיווח לא נשלח.",
      );
    const report = await api.report(command.type, reportFix, "", signal);
    voicePosted.current = { type: command.type, at: Date.now() };
    setReports((prev) => [report, ...prev.filter((r) => r.id !== report.id)]);
    tell(
      `דיווח על ${REPORTS[command.type].label} נשלח במיקום שלך ומופיע אצל כולם`,
    );
    setVoiceMessage(`דיווח על ${REPORTS[command.type].label} נשלח`);
    if (settings.voiceAlerts || handsFree.active)
      speakOwned(
        `דיווח על ${REPORTS[command.type].label} התקבל. תודה.`,
        "community",
        () => liveContext.current.mode !== "simulation",
      );
  }
  function startVoice() {
    if (mode === "simulation") {
      tell("דיווח קולי אינו זמין בסימולציה");
      return;
    }
    setVoicePanel(true);
    setVoiceHelp(false);
    handsFree.stop();
    stopSpeech();
    f5Speech.unlock();
    void currentObservation().catch(() => {});
    void recorder.start({
      question: liveContext.current.confirmationReport || activeReport,
      fix: latestFix.current ? { ...latestFix.current } : null,
    });
  }
  function startVoiceSearch() {
    setVoiceHelp(false);
    setVoicePanel(true); setVoiceMessage("אמרו שם מקום או כתובת. בסיום לחצו על סיום וזיהוי.");
    handsFree.stop(); stopSpeech(); f5Speech.unlock();
    void recorder.start({ searchOnly: true, question: null, fix: null });
  }
  function startHandsFree() {
    if (mode === "simulation") {
      tell("פקודות קוליות זמינות רק מחוץ לסימולציה");
      return;
    }
    setVoicePanel(false);
    setVoiceHelp(false);
    tell("מפעילים מיקרופון · האייקון מציג את מצב ההאזנה");
    setVoiceMessage("");
    recorder.cancel();
    voiceNavigation.reset();
    stopSpeech();
    f5Speech.unlock();
    void handsFree.start();
    // The GPS effect starts only after microphone access, avoiding competing OS prompts.
  }
  function stopHandsFree() {
    handsFree.stop();
    voiceNavigation.reset();
    stopSpeech();
  }
  function beginSave(kind: SavedPlace["kind"]) {
    setSaveKind(kind);
    setSection("explore");
    setQuery("");
    setTimeout(() => searchInput.current?.focus(), 50);
  }
  function shortcut(kind: "home" | "work") {
    const p = saved.find((p) => p.kind === kind);
    if (p) selectPlace(p);
    else beginSave(kind);
  }
  function saveDestination(kind: SavedPlace["kind"]) {
    if (!destination) return;
    try {
      setSaved(savePlace({ ...destination, kind }));
      tell("המקום נוסף למקומות השמורים");
    } catch (e) {
      tell((e as Error).message);
    }
  }
  function mapPick(p: LatLng) {
    if (mode !== "idle") return;
    if (pickMode === "place") { setSuggestionPoint(p); setPickMode("destination"); return; }
    if (pickMode === "origin") {
      chooseOrigin({ ...p, id: `origin-${p.lat}-${p.lng}`, name: "נקודה שנבחרה במפה", address: "" });
      tell("נקודת המוצא עודכנה");
      return;
    }
    if (pickMode === "report") {
      setReportPosition(p);
      setPickMode("destination");
      setReportModal(true);
      return;
    }
    selectPlace({
      id: `point-${p.lat.toFixed(5)}-${p.lng.toFixed(5)}`,
      name: "נקודה על המפה",
      address: `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`,
      ...p,
    });
  }
  function openReport() {
    if (mode === "simulation") {
      tell("במצב סימולציה לא שולחים דיווחים. עצרו את הסימולציה כדי לדווח.");
      return;
    }
    setReportType(null);
    cameraLookup.current?.abort();
    cameraLookup.current = null;
    setReportBusy(false);
    setCameraRemoval(false);
    setCameraRemovalError("");
    setReportNote("");
    setReportPosition(position ?? mapHandle.current?.center() ?? origin);
    setReportModal(true);
  }
  async function openCameraRemoval() {
    if (mode === "simulation") {
      tell("אימות דיווחים זמין רק במיקום אמיתי");
      return;
    }
    cameraLookup.current?.abort();
    const controller = new AbortController();
    cameraLookup.current = controller;
    setCameraRemoval(true);
    setReportType(null);
    setCameraCandidates([]);
    setCameraRemovalError("");
    setReportBusy(true);
    try {
      const here = await currentObservation();
      controller.signal.throwIfAborted();
      setCameraRemovalOrigin(here);
      const nearby = await api.reports(here, controller.signal);
      controller.signal.throwIfAborted();
      setCameraCandidates(observationCandidates(nearby, here, "CAMERA"));
    } catch (error) {
      if (!controller.signal.aborted)
        setCameraRemovalError((error as Error).message);
    } finally {
      if (!controller.signal.aborted) setReportBusy(false);
      if (cameraLookup.current === controller) cameraLookup.current = null;
    }
  }
  async function submitReport() {
    if (!reportType) return;
    setReportBusy(true);
    try {
      const r = await api.report(reportType, reportPosition, reportNote);
      setReports((prev) => [r, ...prev.filter((x) => x.id !== r.id)]);
      setReportModal(false);
      tell("תודה! הדיווח שלך מופיע עכשיו על המפה");
    } catch (e) {
      tell((e as Error).message);
    } finally {
      setReportBusy(false);
    }
  }
  async function confirmReport(r: RoadReport, signal?: AbortSignal) {
    if (mode === "simulation") {
      tell("אימות דיווחים זמין רק במיקום אמיתי");
      return false;
    }
    setReportBusy(true);
    try {
      const observation = await currentObservation();
      signal?.throwIfAborted();
      const result = await api.confirm(r.id, observation, signal);
      setReports((prev) => prev.map((x) => (x.id === r.id ? result : x)));
      setActiveReport((previous) =>
        previous?.id === result.id ? result : previous,
      );
      tell("תודה, האישור שלך התקבל");
      setConfirmationReport(null);
      return true;
    } catch (e) {
      tell((e as Error).message);
      return false;
    } finally {
      setReportBusy(false);
    }
  }
  async function deleteReport(r: RoadReport) {
    setReportBusy(true);
    try {
      await api.removeReport(r.id);
      setReports((prev) => prev.filter((x) => x.id !== r.id));
      setActiveReport(null);
      tell("הדיווח הוסר");
    } catch (e) {
      tell((e as Error).message);
    } finally {
      setReportBusy(false);
    }
  }
  const visibleReports = reports.filter(
    (r) =>
      filters.includes(r.type) &&
      (r.expiresAt === null || r.expiresAt > Date.now()),
  );
  const remaining = progress?.arrived
    ? 0
    : (progress?.remainingDuration ?? route?.duration ?? 0) +
      (mode === "live" && traffic.estimate?.available
        ? traffic.estimate.delaySeconds
        : 0);
  const eta = new Date(Date.now() + remaining * 1000).toLocaleTimeString(
    "he-IL",
    { hour: "2-digit", minute: "2-digit" },
  );
  const currentStep = route?.steps[progress?.stepIndex ?? 0];
  const title =
    section === "saved"
      ? "מקומות שמורים"
      : section === "reports"
        ? "דיווחים"
        : section === "settings"
          ? "הגדרות"
          : "לאן נוסעים?";
  const overlay = transportOpen ? "transport" : hourlyTraffic ? "hourly-traffic" : accountPanel ? "account" : originPicker ? "origin" : routeOverview ? "route-overview" :
    suggestionPoint ? "suggestion" : discovery ? `discovery-${discovery.category}` :
    activeCamera ? `camera-${activeCamera.id}` : activeReport ? `report-${activeReport.id}` :
    reportModal ? `new-report-${reportType || (cameraRemoval ? "remove" : "choose")}` :
    showCatalog ? "catalog" : showFilters ? "filters" : about ? "about" :
    voiceHelp ? "voice-help" : voicePanel ? "voice" : "";
  const viewKind = overlay ? "overlay" : pickMode !== "destination" ? "pick" :
    section !== "explore" ? "section" : query || saveKind ? "search" :
    destination ? "route" : sheetExpanded ? "sheet" : "map";
  const viewKey = overlay ? `overlay:${overlay}` : viewKind === "pick" ? `pick:${pickMode}` :
    viewKind === "section" ? `section:${section}` : viewKind === "route" ? `route:${destination!.id}` :
    viewKind === "search" ? `search:${saveKind || "destination"}` : viewKind;
  useBackNavigation({
    key: viewKey,
    snapshot: { viewKind, section, query, saveKind, pickMode, sheetExpanded, sheetFull, transportOpen, travelMode: settings.travelMode,
      destination, routes, routeIndex, origin, originManual, originName, safetyNotice,
      originPicker, routeOverview, hourlyTraffic, suggestionPoint, discovery, activeCamera, activeReport,
      reportModal, reportType, cameraRemoval, showCatalog, showFilters, about, voiceHelp, voicePanel, accountPanel },
    isDismissal: (previous, next) =>
      (previous.viewKind === "overlay" && next.viewKind !== "overlay") ||
      Boolean(previous.reportType && !next.reportType && next.reportModal) ||
      (["transportOpen", "originPicker", "routeOverview", "hourlyTraffic", "suggestionPoint", "discovery", "activeCamera", "activeReport",
        "reportModal", "showCatalog", "showFilters", "about", "voiceHelp", "voicePanel", "accountPanel"] as const)
        .some(field => Boolean(previous[field]) && !next[field]) ||
      (previous.viewKind === "pick" && next.viewKind !== "pick") ||
      (["search", "sheet", "route"].includes(previous.viewKind) && next.viewKind === "map"),
    onRestore: (frame) => {
      const sameTrip = destination?.id === frame.destination?.id;
      const sameTransport = settings.travelMode === frame.travelMode;
      if (!sameTrip || !sameTransport) {
        stopNavigation();
        setDestination(frame.destination);
      }
      if (mode === "idle" && (origin.lat !== frame.origin.lat || origin.lng !== frame.origin.lng || originManual !== frame.originManual)) {
        routeAbort.current?.abort();
        setRouteBusy(false);
      }
      if (voicePanel && !frame.voicePanel) recorder.cancel();
      setSection(frame.section); setQuery(frame.query); setSaveKind(frame.saveKind);
      setPickMode(frame.pickMode); setSheetExpanded(settings.travelMode === "bus" && !!frame.destination || frame.sheetExpanded); setSheetFull(frame.sheetFull);
      // Closing a screen during driving must retain any GPS reroute that
      // completed while that screen was open.
      if (mode === "idle" || !sameTrip || !sameTransport) {
        setRoutes(sameTransport ? frame.routes : []); setRouteIndex(sameTransport ? frame.routeIndex : 0); setSafetyNotice(sameTransport ? frame.safetyNotice : "");
        manualOrigin.current = frame.originManual;
        setOriginManual(frame.originManual); setOrigin(frame.origin); setOriginName(frame.originName);
        if (!sameTransport && frame.destination) void requestRoutes(frame.origin, frame.destination, settings.travelMode);
      }
      setOriginPicker(frame.originPicker); setRouteOverview(frame.routeOverview); setHourlyTraffic(frame.hourlyTraffic);
      setSuggestionPoint(frame.suggestionPoint); setDiscovery(frame.discovery);
      setActiveCamera(frame.activeCamera); setActiveReport(frame.activeReport);
      setReportModal(frame.reportModal); setReportType(frame.reportType); setCameraRemoval(frame.cameraRemoval);
      setShowCatalog(frame.showCatalog); setShowFilters(frame.showFilters); setAbout(frame.about);
      setVoiceHelp(frame.voiceHelp); setVoicePanel(frame.voicePanel);
      setAccountPanel(frame.accountPanel);
      setTransportOpen(frame.transportOpen);
    },
  });
  const mapReady = useCallback((h: MapHandle) => {
    mapHandle.current = h;
  }, []);
  useEffect(() => {
    if (query || saveKind) setSheetExpanded(true);
    else if (destination) { setSheetExpanded(settings.travelMode === "bus"); setSheetFull(false); }
  }, [query, destination, saveKind, settings.travelMode]);
  useEffect(() => { if (!sheetExpanded) setSheetFull(false); }, [sheetExpanded]);
  useEffect(() => { setRouteOverview(false); }, [destination?.id, mode]);
  useEffect(() => {
    // A different screen must start at its own heading, not the old screen's scroll offset.
    if (sidebarRef.current) sidebarRef.current.scrollTop = 0;
  }, [section]);
  return (
    <div
      className={
        "app " +
        (installedApp ? "installed-app " : "") +
        (sheetExpanded || section !== "explore" ? "sheet-expanded " : "sheet-compact ") +
        (sheetFull ? "sheet-full " : "") + (sheetDrag !== null ? "sheet-dragging " : "") +
        (destination && settings.travelMode !== "bus" && !query && !saveKind && section === "explore" ? "route-ready " : "") +
        section +
        " " +
        (mode !== "idle" ? "navigating " : "") +
        (dark ? "dark" : "")
      }
      style={{ "--sheet-drag": `${sheetDrag ?? 0}px` } as CSSProperties}
    >
      <PermissionSetup onLocation={applyPosition} />
      <header className="topbar">
        <a className="brand" href="/" aria-label="Desty — דף הבית">
          <span className="brand-mark">
            <img src="/icon.svg" width="40" height="40" alt="" />
          </span>
          <span className="brand-name">
            Desty<span className="brand-dot">.</span>
          </span>
          <span className="brand-divider" />
        </a>
        <div className="topbar-left">
          {!installedApp && <a className="download-header-link" href="/download/">
            <Download size={17} />
            <span>הורדת האפליקציה</span>
          </a>}
          <span className="beta-badge">BETA</span>
          <button
            className="avatar"
            onClick={() => setAccountPanel(true)}
            aria-label="חשבון והיסטוריה"
          >
            <UserRound size={18} />
          </button>
        </div>
      </header>
      <div className="workspace">
        <nav className="nav-rail" aria-label="ניווט ראשי">
          {(
            [
              { id: "explore", label: "מפה", icon: Compass },
              { id: "saved", label: "שמורים", icon: Star },
              { id: "reports", label: "דיווחים", icon: Bell },
              { id: "settings", label: "הגדרות", icon: Settings2 },
            ] as { id: Section; label: string; icon: LucideIcon }[]
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={"nav-item " + (section === id ? "active" : "")}
              onClick={() => {
                setSection(id);
                setSheetExpanded(false);
                setSaveKind(null);
              }}
              aria-current={section === id ? "page" : undefined}
            >
              <Icon size={22} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <aside ref={sidebarRef} className="sidebar" id="destination-sheet" aria-label={title}>
          {destination && settings.travelMode !== "bus" && <section className="route-dock" aria-label="סיכום המסלול">
            <div className="route-dock-heading">
              <strong title={destination.name}>{destination.name}</strong>
              <button className="text-btn transport-change" onClick={()=>setTransportOpen(true)} aria-label="שינוי סוג תחבורה">{transportLabels[settings.travelMode]}</button>
              <button className="icon-btn" aria-label="סגירת תכנון המסלול" onClick={finishNavigation}><X size={20}/></button>
            </div>
            {routeBusy ? <p role="status">מחשבים את הדרך…</p> : route ? <>
              <div className="trip-metrics">
                <div><strong>{formatDuration(route.duration)}</strong><small>זמן משוער</small></div>
                <div><strong>{formatDistance(route.distance)}</strong><small>מרחק</small></div>
                <div><strong>{eta}</strong><small>הגעה משוערת</small></div>
              </div>
              <button className="route-origin-note" onClick={openOriginPicker} aria-label="שינוי נקודת מוצא"><MapPin size={14}/><span>מ־{originLabel}</span><Pencil size={13}/></button>
              <div className="route-dock-actions">
                <button className="outline-btn" onClick={() => setRouteOverview(true)}><RouteIcon size={20}/>מסלול</button>
                <button className="primary-btn" onClick={() => void startNavigation()} disabled={gpsBusy}><Navigation size={20}/>{gpsBusy ? "מאתרים מיקום…" : originManual ? "ניווט מהמיקום שלי" : "יציאה לדרך"}</button>
              </div>
            </> : <div className="route-dock-actions"><span>{gpsBusy ? "מאתרים את המיקום שלך…" : "נדרשת נקודת מוצא למסלול"}</span><button className="outline-btn" onClick={openOriginPicker}>בחירת מוצא</button></div>}
          </section>}
          <SheetHandle level={sheetFull ? 2 : sheetExpanded || section !== "explore" ? 1 : 0}
            onDrag={setSheetDrag} onChange={level => {
              searchInput.current?.blur();
              if (level === 0) setSection("explore");
              setSheetFull(level === 2); setSheetExpanded(level > 0);
            }} />
          <div className="sidebar-heading">
            <h1>{title}</h1>
          </div>
          {section === "explore" && (
            <>
              {saveKind && (
                <div className="inline-banner">
                  בחרו מקום ל
                  {saveKind === "home"
                    ? "בית"
                    : saveKind === "work"
                      ? "עבודה"
                      : "מועדפים"}
                  <button
                    onClick={() => setSaveKind(null)}
                    aria-label="ביטול שמירת מקום"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
              <TransportPicker value={settings.travelMode} onChange={changeTransport}/>
              {isCycling(settings.travelMode) && <label className="cycleway-legend"><input type="checkbox" checked={settings.showCycleways} onChange={e=>updateSettings({showCycleways:e.target.checked})}/><i/>שבילי אופניים במפה · ירוק מקווקו</label>}
              <button className="planner-origin" onClick={openOriginPicker}><LocateFixed size={16}/><span>מוצא: {originLabel}</span><Pencil size={14}/></button>
              <div className="search-box">
                <button className="search-focus" onClick={() => searchInput.current?.focus()} aria-label="חיפוש מקום" title="חיפוש מקום"><Search size={21} /></button>
                <input
                  ref={searchInput}
                  onFocus={() => setSheetExpanded(true)}
                  enterKeyHint="search"
                  value={query}
                  maxLength={4096}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={
                    saveKind
                      ? "חפשו כתובת לשמירה"
                      : "שם מקום, כתובת או קישור למפה…"
                  }
                  aria-label="חיפוש כתובת או מקום"
                />
                {query ? (
                  <button
                    aria-label="ניקוי חיפוש"
                    onClick={() => {
                      setQuery("");
                    }}
                  >
                    <X size={17} />
                  </button>
                ) : null}
                <button className="search-mic" onClick={startVoiceSearch} aria-label="חיפוש קולי" title="חיפוש קולי"><Mic size={21} /></button>
              </div>
              {query.length > 0 && (
                <div className="search-results">
                  {!online && <p className="muted" role="status">ללא קליטה · חיפוש במקומות שהורדו למכשיר</p>}
                  {query.trim().length < 2 ? (
                    <p className="muted">הקלידו לפחות 2 תווים לחיפוש</p>
                  ) : searching ? (
                    <div className="loading">
                      <LoaderCircle className="spin" size={18} />
                      מחפשים את המקום שלך…
                    </div>
                  ) : searchError ? (
                    <p className="error-text">{searchError}</p>
                  ) : results.length === 0 ? (
                    <p className="muted">
                      לא נמצאו מקומות. נסו להוסיף עיר, כתובת מלאה או להדביק
                      קישור למיקום.
                    </p>
                  ) : (
                    results.map((p) => (
                      <button
                        className="place-row"
                        key={p.id}
                        onClick={() => selectPlace(p)}
                      >
                        <span className="place-icon">
                          <MapPin size={18} />
                        </span>
                        <span>
                          <strong>{p.name}</strong>
                          <small>{p.address}</small>
                        </span>
                        <ChevronLeft size={16} />
                      </button>
                    ))
                  )}
                </div>
              )}
              {settings.travelMode === "bus" && <TransitPanel origin={origin} destination={destination} onStops={setTransitStops} onLocate={stop=>mapHandle.current?.locate(stop)}/>}
              <div className="quick-destinations">
                <button onClick={()=>setTransportOpen(true)} aria-label="בחירת סוג תחבורה"><span className="quick-icon"><RouteIcon size={18}/></span><strong>{transportLabels[settings.travelMode]}</strong></button>
                <button onClick={() => shortcut("home")}>
                  <span className="quick-icon">
                    <Home size={23} />
                  </span>
                  <strong>הביתה</strong>
                  <small>
                    {saved.find((p) => p.kind === "home")?.name || "הוספת בית"}
                    {saved.some((p) => p.kind === "home") ? <ChevronLeft size={11} /> : <Plus size={11} />}
                  </small>
                </button>
                <button onClick={() => shortcut("work")}>
                  <span className="quick-icon peach">
                    <BriefcaseBusiness size={23} />
                  </span>
                  <strong>לעבודה</strong>
                  <small>
                    {saved.find((p) => p.kind === "work")?.name || "הוספת עבודה"}
                    {saved.some((p) => p.kind === "work") ? <ChevronLeft size={11} /> : <Plus size={11} />}
                  </small>
                </button>
                <button onClick={() => setSection("saved")}>
                  <span className="quick-icon lavender">
                    <Star size={23} />
                  </span>
                  <strong>מועדפים</strong>
                  <small>
                    {saved.filter((p) => p.kind === "favorite").length ||
                      "כל המקומות"}
                    <ChevronLeft size={11} />
                  </small>
                </button>
              </div>
              <div className="direct-place-tools" aria-label="פעולות נוספות לדרך">
                <button onClick={() => discover("parking")}><ParkingCircle size={20}/><span>חניה קרובה</span></button>
                <button onClick={() => discover("shelter")}><ShieldCheck size={20}/><span>מקלט קרוב</span></button>
                <button onClick={suggestPlace} disabled={mode !== "idle"}><MapPin size={20}/><span>הצעת מקום</span></button>
                <button onClick={() => discover("food")}><Coffee size={20} /><span>אוכל וקפה</span></button>
                <button onClick={() => setVoiceHelp(true)}><Mic size={20} /><span>עזרה בקול</span></button>
                <button onClick={openOriginPicker}><Flag size={20} /><span>נקודת מוצא</span></button>
              </div>
              {destination ? (
                <div className="trip-panel">
                  <div className="section-title">
                    <h2>{mode === "idle" ? "פרטי המסלול" : "בדרך ליעד"}</h2>
                    <button
                      className="icon-btn"
                      aria-label="סגירת מסלול"
                      onClick={() => {
                        stopNavigation();
                        setDestination(null);
                        setRoutes([]);
                        routeAbort.current?.abort();
                        setRouteBusy(false);
                      }}
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="trip-destination">
                    <span className="place-icon green">
                      <Flag size={20} />
                    </span>
                    <div>
                      <strong>{destination.name}</strong>
                      <small>{destination.address}</small>
                    </div>
                    <button
                      className={
                        "icon-btn " +
                        (saved.some((p) => p.id === destination.id)
                          ? "starred"
                          : "")
                      }
                      onClick={() => saveDestination("favorite")}
                      disabled={isGooglePlace(destination)}
                      title={
                        isGooglePlace(destination)
                          ? "המקום זמין לנסיעה הזו בלבד"
                          : undefined
                      }
                      aria-label="שמירה במועדפים"
                    >
                      <Star size={19} />
                    </button>
                  </div>
                  <button
                    className="origin-label"
                    onClick={openOriginPicker}
                  >
                    <span className="small-dot" />
                    {originLabel}
                    <Pencil size={12} />
                  </button>
                  {settings.travelMode === "bus" ? null : routeBusy ? (
                    <div className="loading">
                      <LoaderCircle className="spin" size={20} />
                      מחשבים את הדרך…
                    </div>
                  ) : routes.length === 0 ? (
                    <div className="empty-route">
                      <p>{online ? "לא הצלחנו לחשב מסלול כרגע" : "חישוב מסלול חדש דורש חיבור לשרתי Desty"}</p>
                      <button
                        className="text-btn"
                        onClick={() => void selectPlace(destination)}
                      >
                        ניסיון נוסף <RefreshCw size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="route-options">
                        {routes.slice(0, 3).map((r, i) => (
                          <button
                            key={r.id}
                            className={
                              "route-option " +
                              (routeIndex === i ? "selected" : "")
                            }
                            disabled={mode !== "idle"}
                            onClick={() => setRouteIndex(i)}
                          >
                            <span className="route-option-title">
                              {i === 0 ? "המסלול המומלץ" : `חלופה ${i}`}{" "}
                              {routeIndex === i && <Check size={14} />}
                            </span>
                            <strong>{formatDuration(r.duration)}</strong>
                            <small>{formatDistance(r.distance)}</small>
                          </button>
                        ))}
                      </div>
                      <p className="route-disclaimer">
                        {traffic.estimate?.available
                          ? `עודכן מנהגים בדרך · ${traffic.estimate.driverCount} נהגים`
                          : usesRoadTraffic(settings.travelMode) ? "הערכת מסלול · תנועה חיה תתווסף כשיהיו מספיק מדידות" : `הערכת זמן · ${transportLabels[settings.travelMode]}`}
                      </p>
                      <button className="outline-btn" onClick={() => setRouteOverview(true)}><RouteIcon size={20}/>מסלול · כל הפניות</button>
                      {safetyNotice && (
                        <p className="safety-note">
                          <ShieldCheck size={15} />
                          {safetyNotice}
                        </p>
                      )}
                      {mode === "idle" ? (
                        <>
                          <button
                            className="primary-btn start-btn"
                            onClick={() => void startNavigation()}
                            disabled={gpsBusy}
                          >
                            {gpsBusy ? (
                              <LoaderCircle className="spin" size={19} />
                            ) : (
                              <Navigation size={19} />
                            )}
                            {originManual ? "ניווט מהמיקום שלי" : "יציאה לדרך"}
                            <ArrowLeft size={19} />
                          </button>
                          <button
                            className="simulation-btn"
                            onClick={startSimulation}
                          >
                            <Play size={13} />
                            לנסות את המסלול בסימולציה
                          </button>
                        </>
                      ) : (
                        <div className="trip-active">
                          <span className="status-dot" />
                          <strong>
                            {progress?.arrived
                              ? "הגעת ליעד!"
                              : mode === "simulation"
                                ? "סימולציה פועלת"
                                : "הניווט פעיל"}
                          </strong>
                          <button
                            className="text-btn"
                            onClick={finishNavigation}
                          >
                            סיום נסיעה
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="section-title">
                    <h2>
                      {recents.length ? account.user ? "ניווטים אחרונים" : "חיפושים אחרונים" : "יעדים"}
                    </h2>
                    <span className="small-label">
                    </span>
                  </div>
                  <div className="places-list">
                    {(recents.length ? recents.slice(0, sheetFull ? 20 : 6) : PICKS).map(
                      (p, i) => (
                        <button
                          className="place-row"
                          key={p.id}
                          onClick={() => selectPlace(p)}
                        >
                          <span
                            className={
                              "place-icon " + ["mint", "peach", "lavender"][i]
                            }
                          >
                            {recents.length ? (
                              <Clock3 size={20} />
                            ) : i === 0 ? (
                              <Compass size={20} />
                            ) : i === 1 ? (
                              <Trees size={20} />
                            ) : (
                              <MapPin size={20} />
                            )}
                          </span>
                          <span>
                            <strong>{p.name}</strong>
                            <small>{p.address}</small>
                          </span>
                          <ChevronLeft size={17} />
                        </button>
                      ),
                    )}
                  </div>
                  <div className="nearby-categories">
                    <span>בסביבה</span>
                    <div>
                      <button onClick={() => discover("fuel")}>
                        <Fuel size={15} />
                        דלק
                      </button>
                      <button onClick={() => discover("food")}>
                        <Coffee size={15} />
                        אוכל
                      </button>
                      <button onClick={() => setQuery("חניון")}>
                        <ParkingCircle size={15} />
                        חניה
                      </button>
                      <button onClick={() => discover("nature")}>
                        <Trees size={15} />
                        ירוק
                      </button>
                      <button onClick={() => discover("attractions")}>
                        <Sparkles size={15} />
                        לגלות
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          {section === "saved" && (
            <>
              <button className="account-history-link" onClick={() => setAccountPanel(true)}><Clock3 size={21} /><span>חשבון והיסטוריה</span><ChevronLeft size={18} /></button>
              <div className="saved-top">
                <button
                  className="saved-special"
                  onClick={() => shortcut("home")}
                >
                  <Home size={23} />
                  <strong>בית</strong>
                  <small>
                    {saved.find((p) => p.kind === "home")?.name ||
                      "מגדירים פעם אחת, מגיעים בקלות"}
                  </small>
                </button>
                <button
                  className="saved-special peach"
                  onClick={() => shortcut("work")}
                >
                  <BriefcaseBusiness size={23} />
                  <strong>עבודה</strong>
                  <small>
                    {saved.find((p) => p.kind === "work")?.name ||
                      "הכתובת של שגרת היום"}
                  </small>
                </button>
              </div>
              <div className="section-title">
                <h2>כל המקומות השמורים</h2>
                <button
                  className="icon-btn"
                  onClick={() => beginSave("favorite")}
                  aria-label="הוספת מועדף"
                >
                  <Plus size={20} />
                </button>
              </div>
              {saved.length === 0 ? (
                <div className="empty-state">
                  <Star size={35} />
                  <h3>יש מקומות שכיף לחזור אליהם</h3>
                  <p>שמרו כתובת מהחיפוש או נקודה על המפה, והיא תחכה לכם כאן.</p>
                  <button
                    className="primary-btn"
                    onClick={() => beginSave("favorite")}
                  >
                    הוספת מקום ראשון
                    <Plus size={17} />
                  </button>
                </div>
              ) : (
                saved.map((p) => (
                  <div className="saved-row" key={p.id}>
                    <button
                      className="place-row"
                      onClick={() => selectPlace(p)}
                    >
                      <span className="place-icon">
                        <Star size={19} />
                      </span>
                      <span>
                        <strong>{p.name}</strong>
                        <small>
                          {p.kind === "home"
                            ? "בית"
                            : p.kind === "work"
                              ? "עבודה"
                              : p.address}
                        </small>
                      </span>
                    </button>
                    <button
                      className="icon-btn"
                      aria-label={`מחיקת ${p.name}`}
                      onClick={() => {
                        setSaved(removePlace(p.id));
                        tell("המקום הוסר מהמקומות השמורים");
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
              <button
                className="outline-btn full-width"
                onClick={() => beginSave("favorite")}
              >
                <Plus size={17} />
                מקום חדש
              </button>
            </>
          )}
          {section === "reports" && (
            <>
              <div className="reports-summary">
                <span className="summary-icon">
                  <Bell size={23} />
                </span>
                <div>
                  <strong>{visibleReports.length} דיווחים פעילים</strong>
                  <small>ברדיוס 15 ק״מ · נשארים עד שהאירוע מסתיים</small>
                </div>
                <button
                  className="icon-btn"
                  onClick={() => setReportVersion((v) => v + 1)}
                  aria-label="רענון דיווחים"
                >
                  <RefreshCw size={18} />
                </button>
              </div>
              <div className={"live-status " + communityState}>
                <Radio size={14} />
                {communityState === "live"
                  ? "דיווח חדש מתעדכן עכשיו אצל כולם"
                  : communityState === "polling"
                  ? "הדיווחים מתעדכנים אוטומטית · החיבור המיידי מתחדש"
                  : "מתחברים לדיווחים החיים…"}
              </div>
              {reportError && <p className="error-text">{reportError}</p>}
              <div className="report-chips">
                {reportTypes.map((type) => {
                  const Icon = REPORTS[type].icon;
                  return (
                    <button
                      key={type}
                      className={filters.includes(type) ? "selected" : ""}
                      onClick={() =>
                        setFilters((f) =>
                          f.includes(type)
                            ? f.filter((x) => x !== type)
                            : [...f, type],
                        )
                      }
                    >
                      <Icon size={14} />
                      {REPORTS[type].label}
                    </button>
                  );
                })}
              </div>
              {visibleReports.length === 0 ? (
                <div className="empty-state">
                  <ShieldCheck size={36} />
                  <h3>כרגע אין דיווחים באזור</h3>
                  <p>
                    היעדר דיווחים אינו מעיד שהכביש פנוי.
                  </p>
                </div>
              ) : (
                visibleReports.map((r) => {
                  const info = REPORTS[r.type],
                    Icon = info.icon;
                  return (
                    <button
                      key={r.id}
                      className="place-row report-list-item"
                      onClick={() => {
                        setActiveReport(r);
                        mapHandle.current?.locate(r);
                      }}
                    >
                      <span className={"place-icon " + info.color}>
                        <Icon size={20} />
                      </span>
                      <span>
                        <strong>{info.label}</strong>
                        <small>
                          {r.note || info.detail} · לפני{" "}
                          {Math.max(
                            1,
                            Math.floor((Date.now() - r.createdAt) / 60000),
                          )}{" "}
                          דק׳
                        </small>
                      </span>
                      <ChevronLeft size={16} />
                    </button>
                  );
                })
              )}
              <button className="primary-btn full-width" onClick={openReport}>
                <Plus size={18} />
                הוספת דיווח
              </button>
            </>
          )}
          {section === "settings" && (
            <>
              <button className="account-history-link" onClick={() => setAccountPanel(true)}><UserRound size={21} /><span>חשבון והיסטוריה<small>{account.user ? account.user.username : "ניווט כאורח · כניסה והרשמה"}</small></span><ChevronLeft size={18} /></button>
              <div className="settings-card speed-settings">
                <h2>מהירות והתראות</h2>
                {([
                  ["showSpeed", "הצגת מד מהירות", "מהירות GPS לצד המהירות המותרת, כשידועה"],
                  ["speedWarning", "התראה חזותית בחריגה", "הדגשה גם כאשר מד המהירות מוסתר"],
                  ["speedSound", "צפצוף בחריגה", "צליל קצר בתחילת חריגה; ללא הקראה"],
                ] as const).map(([key,label,help]) => <div className="setting-row" key={key}><div><strong>{label}</strong><small>{help}</small></div><Toggle label={label} checked={settings[key]} onChange={()=>updateSettings({[key]:!settings[key]})}/></div>)}
                <label className="speed-threshold">מתי להתריע<select value={settings.speedTolerance} onChange={e=>updateSettings({speedTolerance:Number(e.target.value)})}><option value={0}>בחציית המהירות המותרת</option><option value={5}>בחריגה של 5 קמ״ש</option><option value={10}>בחריגה של 10 קמ״ש</option></select></label>
                <p className="settings-hint">כשאין נתון או מיקום מדויק, מוצג קו ולא מופעלת התראה. תמרורי הדרך קובעים.</p>
              </div>
              <div className="settings-card">
                <h2>בזמן הנסיעה</h2>
                <div className="setting-row">
                  <Volume2 size={21} />
                  <div>
                    <strong>הנחיות קוליות</strong>
                    <small>
                      {settings.speechProvider === "f5"
                        ? "הקראה בעברית"
                        : "הקראה בקול המכשיר"}
                    </small>
                  </div>
                  <Toggle
                    checked={settings.voiceAlerts}
                    label="הנחיות קוליות"
                    onChange={() =>
                      updateSettings({ voiceAlerts: !settings.voiceAlerts })
                    }
                  />
                </div>
                <div className="setting-row">
                  <Mic size={21} />
                  <div>
                    <strong>פקודות ללא מגע</strong>
                    <small>״משטרה״, ״מצלמה״ או ״ניווט״ ואז כתובת ו״אנטר״</small>
                    <button className="text-btn voice-help-link" onClick={() => setVoiceHelp(true)}><CircleHelp size={16} /> איך משתמשים בקול?</button>
                  </div>
                  <Toggle
                    checked={handsFree.active}
                    label="האזנה רציפה"
                    onChange={handsFree.active ? stopHandsFree : startHandsFree}
                  />
                </div>
                <div className="voice-engine-picker">
                  <strong>בדיקות שמע</strong>
                  <small>כל כפתור בודק מקור קול אחד. ההנחיות בנסיעה משתמשות בקול המכשיר עם גיבוי מהשרת.</small>
                  {(["device", "server"] as const).map(source => <button
                    key={source}
                    className="text-btn"
                    onClick={() => {
                      f5Speech.unlock();
                      speakOwned(
                        "בדיקת קול. פנו ימינה בצומת הבא.",
                        "community",
                        () => !document.hidden,
                        undefined,
                        undefined,
                        true,
                        source,
                      );
                    }}
                  >
                    {source === "device" ? "בדיקת קול המכשיר" : "בדיקת קול השרת"} <Volume2 size={15} />
                  </button>)}
                  {speechPlaying && <button className="text-btn" onClick={() => stopSpeech()}>הפסקת השמע</button>}
                  {speechStatus && <small role="status">{speechStatus}</small>}
                </div>
                <div className="setting-row">
                  <ShieldCheck size={21} />
                  <div>
                    <strong>הימנעות מדרכים מסוכנות</strong>
                    <small>חסימות, תאונות ומפגעים פעילים שדווחו בקהילה</small>
                  </div>
                  <Toggle
                    checked={settings.avoidDangers}
                    label="הימנעות מדרכים מסוכנות"
                    onChange={() => {
                      const enabled = !settings.avoidDangers;
                      updateSettings({ avoidDangers: enabled });
                      setSafetyNotice("ההעדפה תחול בחישוב המסלול הבא");
                      if (mode !== "idle") stopNavigation();
                      setRoutes([]);
                    }}
                  />
                </div>
                <div className="setting-row">
                  <TrafficCone size={21} />
                  <div>
                    <strong>שיתוף תנועה וזמני נסיעה</strong>
                    <small>
                      בניווט אמיתי בלבד: מיקום, מהירות וכיוון נשמרים עד 5 דקות.
                      זמני נסיעה שהושלמה נשמרים עד 56 ימים, ללא מסלול המיקומים האישי,
                      כדי להציג ממוצע לפי שעה אחרי שנאספו נתונים מ־3 התקנות שונות לפחות.
                    </small>
                  </div>
                  <Toggle
                    checked={settings.shareTraffic}
                    label="שיתוף נתוני תנועה"
                    onChange={() =>
                      updateSettings({ shareTraffic: !settings.shareTraffic })
                    }
                  />
                </div>
                <div className="setting-row">
                  <Bell size={21} />
                  <div>
                    <strong>התראות דרך</strong>
                    <small>דיווחי קהילה במרחק של עד 400 מטר</small>
                  </div>
                  <Toggle
                    checked={settings.reportAlerts}
                    label="התראות דרך"
                    onChange={() =>
                      updateSettings({ reportAlerts: !settings.reportAlerts })
                    }
                  />
                </div>
              </div>
              <div className="settings-card">
                <h2>מראה ונוחות</h2>
                <div className="setting-row">
                  {dark ? <Moon size={21} /> : <Sun size={21} />}
                  <div>
                    <strong>מצב לילה</strong>
                    <small>ממשק כהה לנסיעות ערב</small>
                  </div>
                  <Toggle
                    checked={dark}
                    label="מצב לילה"
                    onChange={() => setDark(!dark)}
                  />
                </div>
                <div className="setting-row">
                  <Map size={21} />
                  <div>
                    <strong>שפת הממשק</strong>
                    <small>עברית • קילומטרים • שעון 24 שעות</small>
                  </div>
                  <span className="small-label">עב</span>
                </div>
              </div>
              <div className="privacy-note">
                <ShieldCheck size={22} />
                <h3>פרטיות</h3>
                <p>
                  בית, עבודה ומועדפים נשמרים במכשיר. חיפוש וחישוב מסלול שולחים
                  את הכתובת והנקודות לשירותי המפה. דיווחים שתשלחו יופיעו לקהילה.{" "}
                  באפליקציה משתמשים קודם בזיהוי המכשיר, שעשוי לפנות ל־Google או Apple. בכשל, או בדפדפן ללא תמיכה, קטעי דיבור נשלחים לשרת Desty לזיהוי. במסך הרכב ההאזנה
                  מתחילה אוטומטית לאחר הרשאת מיקרופון. שיתוף נתוני תנועה כבוי עד
                  שתפעילו אותו, ומדידות נשמרות בשרת עד חמש דקות.
                </p>
                <p>התחברות היא לבחירתכם. היסטוריית אורח נשמרת במכשיר; לאחר התחברות, ניווטים חדשים נשמרים בחשבון וניתנים למחיקה בחשבון והיסטוריה.</p>
                <div className="legal-links"><a href="/privacy/">מדיניות פרטיות</a><a href="/terms/">תנאי שימוש</a><a href="/delete-account/">מחיקת חשבון</a></div>
              </div>
              <button
                className="outline-btn full-width"
                onClick={() => setAbout(true)}
              >
                אודות Desty
                <ArrowUpRight size={16} />
              </button>
            </>
          )}
        </aside>
        <main className="map-panel">
          <MapView
            transitStops={settings.travelMode === "bus" ? transitStops : []}
            showCycleways={isCycling(settings.travelMode) && settings.showCycleways}
            position={displayPosition}
            origin={origin}
            destination={destination}
            route={route}
            reports={visibleReports}
            cameras={showCameraLayer ? cameraCatalog?.cameras || [] : []}
            onCamera={setActiveCamera}
            nearbyPlaces={nearbyPlaces}
            onPlace={selectDiscovery}
            follow={mode !== "idle"}
            tileUrl={tileUrl}
            onPick={mapPick}
            onReport={setActiveReport}
            onReady={mapReady}
            onTileError={() => setTileError(true)}
          />
          <Speedometer fix={mode === "simulation" ? null : fix} settings={isCycling(settings.travelMode) || settings.travelMode === "bus" ? {...settings, speedWarning:false, speedSound:false} : settings} onSettings={() => { setSection("settings"); setSheetExpanded(true); }} />
          <div className="map-top">
            <div className="map-location">
              <span className="location-symbol">
                <MapPin size={20} />
              </span>
              <div>
                <strong>
                  {mode === "simulation"
                    ? "סימולציית נסיעה"
                    : mode === "live"
                      ? "המסלול שלך"
                      : originManual
                        ? "מוצא נבחר"
                        : position
                          ? "סביב המיקום שלך"
                          : "מפת הדרך"}
                </strong>
                <small>
                  {mode === "simulation"
                    ? "הדמיה בלבד · ללא מיקום אמיתי"
                    : position
                      ? "בחירת יעד בלחיצה על המפה"
                      : originManual
                        ? "לחצו על מקום במפה כדי לנווט"
                        : "נקודת התחלה · הפעילו מיקום לניווט"}
                </small>
              </div>
              <span className="location-status" />
            </div>
            <button
              className={"map-pill " + (showFilters ? "pressed" : "")}
              onClick={() => setShowFilters(!showFilters)}
              aria-label="מה רואים במפה"
            >
              <SlidersHorizontal size={17} />
              <span>מה רואים במפה</span>
            </button>
          </div>
          {showFilters && (
            <Modal label="דיווחים על המפה" onClose={() => setShowFilters(false)}>
              {reportTypes.map((type) => {
                const Icon = REPORTS[type].icon;
                return (
                  <div className="filter-row" key={type}>
                    <Icon size={18} />
                    <span>{REPORTS[type].label}</span>
                    <Toggle
                      checked={filters.includes(type)}
                      label={REPORTS[type].label}
                      onChange={() =>
                        setFilters((f) =>
                          f.includes(type)
                            ? f.filter((x) => x !== type)
                            : [...f, type],
                        )
                      }
                    />
                  </div>
                );
              })}
              <div className="filter-row">
                <Camera size={18} />
                <span>מצלמות דרך</span>
                <Toggle
                  checked={showCameraLayer}
                  label="הצגת מצלמות דרך"
                  onChange={() => setShowCameraLayer(!showCameraLayer)}
                />
              </div>
              <button
                className="text-btn"
                onClick={() => {
                  setShowFilters(false);
                  setShowCatalog(true);
                }}
              >
                כל המצלמות · {cameraCatalog?.cameras.length ?? "…"}
              </button>
            </Modal>
          )}
          {pickMode !== "destination" && (
            <div className="map-instruction">
              <MapPin size={18} />
              {pickMode === "origin"
                ? "לחצו על המפה לבחירת נקודת המוצא"
                : pickMode === "place" ? "לחצו על המפה לבחירת המקום החדש" : "לחצו על המפה לבחירת מיקום הדיווח"}
              <button
                onClick={() => setPickMode("destination")}
                aria-label="ביטול בחירת נקודה"
              >
                <X size={17} />
              </button>
            </div>
          )}
          {mode !== "idle" && route && (
            <div className="navigation-card">
              <div className="turn-icon">
                {progress?.arrived ? (
                  <Flag size={30} />
                ) : (
                  <ManeuverIcon step={currentStep} size={30} />
                )}
              </div>
              <div>
                <small>
                  {progress?.arrived
                    ? "טוב שהגעת"
                    : mode === "simulation"
                      ? "סימולציה • " +
                        formatDistance(progress?.distanceToStep ?? 0)
                      : formatDistance(progress?.distanceToStep ?? 0)}
                </small>
                <strong>
                  {progress?.arrived
                    ? "הגעת ליעד שלך"
                    : guidanceInstruction(currentStep)}
                </strong>
                {gpsIssue && <small className="gps-issue">{gpsIssue}</small>}
                {speechStatus && (
                  <small className="speech-status" role="status">
                    {speechStatus}
                  </small>
                )}
              </div>
              <button
                className="icon-btn"
                onClick={() =>
                  updateSettings({ voiceAlerts: !settings.voiceAlerts })
                }
                aria-label={settings.voiceAlerts ? "השתקת הנחיות" : "הפעלת קול"}
              >
                {settings.voiceAlerts ? (
                  <Volume2 size={20} />
                ) : (
                  <VolumeX size={20} />
                )}
              </button>
              {!progress?.arrived && <LaneGuidance step={currentStep} />}
              {cameraAlert && (
                <div className="camera-route-alert" role="status">
                  <Camera size={25} />
                  <div>
                    <strong>
                      {cameraLabel(cameraAlert.camera)} ·{" "}
                      {formatDistance(cameraAlert.distanceMeters)}
                    </strong>
                    <small>
                      {mode === "simulation" ? "הדמיה · " : ""}בהמשך המסלול ·
                      לפי המאגר
                    </small>
                  </div>
                  <button
                    className="icon-btn"
                    onClick={() => setActiveCamera(cameraAlert.camera)}
                    aria-label="פרטי המצלמה בהמשך המסלול"
                  >
                    <ChevronLeft size={19} />
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="map-controls">
            <button onClick={() => discover("parking")} aria-label="חניה, מקלטים ומקומות בסביבה" title="מקומות בסביבה"><ParkingCircle size={23} /></button>
            <button onClick={() => setShowCatalog(true)} aria-label="מצלמות בדרך" title="מצלמות בדרך"><Camera size={23} /></button>
            <button
              onClick={() => void locate()}
              aria-label="המיקום שלי"
              className={gpsBusy ? "locating" : ""}
            >
              {gpsBusy ? (
                <LoaderCircle className="spin" size={21} />
              ) : (
                <LocateFixed size={22} />
              )}
            </button>
            <div className="zoom-buttons">
              <button
                onClick={() => mapHandle.current?.zoom(1)}
                aria-label="הגדלת מפה"
              >
                <Plus size={21} />
              </button>
              <span />
              <button
                onClick={() => mapHandle.current?.zoom(-1)}
                aria-label="הקטנת מפה"
              >
                <Minus size={21} />
              </button>
            </div>
          </div>
          <div className="map-bottom">
            {mode !== "idle" && route ? (
              <div className="eta-card">
                <div className="eta-summary">
                <div>
                  <strong>
                    {progress?.arrived
                      ? "הגעת ליעד"
                      : formatDuration(remaining)}
                  </strong>
                  <small>
                    {progress?.arrived ? (
                      "הנסיעה הושלמה · דרך טובה!"
                    ) : (
                      <>
                        {formatDistance(
                          progress?.remainingDistance ?? route.distance,
                        )}{" "}
                        · הגעה {eta}
                      </>
                    )}
                  </small>
                  {mode === "live" && !progress?.arrived && (
                    <span
                      className={
                        "traffic-status " +
                        (traffic.estimate?.available ? "measured" : "")
                      }
                    >
                      {traffic.estimate?.available
                        ? `תנועה חיה · ${traffic.estimate.driverCount} נהגים${traffic.estimate.delaySeconds > 30 ? ` · תוספת ${formatDuration(traffic.estimate.delaySeconds)}` : ""}`
                        : usesRoadTraffic(settings.travelMode) ? traffic.error || "אין עדיין מספיק מדידות תנועה" : `הערכת זמן · ${transportLabels[settings.travelMode]}`}
                    </span>
                  )}
                </div>
                <button className="route-open" onClick={() => setRouteOverview(true)}><RouteIcon size={20}/>מסלול</button>
                <button onClick={finishNavigation}>
                  סיום
                  <X size={16} />
                </button>
                </div>
              </div>
            ) : (
              <div className="map-note">
                <span className="note-symbol">
                  <RouteIcon size={22} />
                </span>
                <div>
                  <strong>לאן נוסעים?</strong>
                  <small>חפשו מקום, או בחרו נקודה על המפה</small>
                </div>
              </div>
            )}
            <div className="report-actions">
              <button
                className={
                  "voice-fab " +
                  (handsFree.active ? "listening " : "") +
                  (voiceBusy ? "recording" : "")
                }
                onClick={() =>
                  handsFree.active
                    ? stopHandsFree()
                    : startHandsFree()
                }
                aria-label={
                  handsFree.active
                    ? "כיבוי האזנה רציפה"
                    : "הפעלת האזנה רציפה"
                }
                aria-pressed={handsFree.active}
                title={handsFree.active ? "האזנה פעילה · לחצו לכיבוי" : "הפעלת האזנה רציפה"}
              >
                {handsFree.state === "transcribing" ||
                handsFree.state === "acting" ? (
                  <LoaderCircle className="spin" size={21} />
                ) : (
                  <Mic size={23} />
                )}
                <span>{handsFree.active ? "האזנה פעילה" : "ללא מגע"}</span>
              </button>
              <button className="report-fab" onClick={openReport} aria-label="דיווח בדרך" title="דיווח בדרך">
                <TriangleAlert size={29} />
                <span>דיווח בדרך</span>
              </button>
            </div>
          </div>
          {confirmationReport && (
            <section
              className="community-question"
              aria-label="אימות דיווח בדרך"
            >
              <header className="panel-toolbar">
                <span className="live-eyebrow">
                  <Radio size={16} /> הנהגים שיבואו אחריך ישמחו לדעת
                </span>
                <button
                  className="question-close"
                  aria-label="דלג על אימות"
                  onClick={() => setConfirmationReport(null)}
                >
                  <X size={16} />
                </button>
              </header>
              <h3>{reportQuestion(confirmationReport)}</h3>
              <p>אפשר לומר ״עדיין כאן״ או ״כבר אין״</p>
              <div className="question-actions">
                <button
                  disabled={reportBusy}
                  onClick={() => void confirmReport(confirmationReport)}
                >
                  <Check size={16} />
                  עדיין כאן
                </button>
                <button
                  disabled={reportBusy}
                  onClick={() =>
                    void resolveReport(confirmationReport).catch(() => {})
                  }
                >
                  <X size={16} />
                  כבר אין
                </button>
                <button onClick={startVoice} aria-label="מענה קולי לאימות">
                  <Mic size={19} />
                </button>
              </div>
            </section>
          )}
          {(voicePanel || recorder.state !== "idle" || handsFree.error || recorder.error || (handsFree.active && (voiceMessage || handsFree.state !== "listening"))) && !voiceHelp && createPortal(
            <section className="voice-compact" aria-label="מצב הפקודות הקוליות">
              <Mic size={20} aria-hidden="true" />
              <div className="voice-compact-copy" role="status">
                <strong>{handsFree.error || recorder.error ? "לא ניתן להאזין כרגע" : recorder.state === "recording" ? "מקשיבים לך…" : handsFree.state === "requesting" ? "ממתינים להרשאת מיקרופון…" : handsFree.state === "paused" ? "האזנה מושהית בזמן ההקראה" : voiceBusy ? "מזהים ומבצעים…" : handsFree.active ? "האזנה פעילה" : "פקודה קולית"}</strong>
                {voiceMessage && <small>{voiceMessage}</small>}
                {(handsFree.error || recorder.error) && <small className="error-text">{handsFree.error || recorder.error}</small>}
              </div>
              {recorder.state === "recording" && <button className="voice-finish" onClick={() => recorder.stop()}><Square size={16} /> סיום וזיהוי</button>}
              <button className="icon-btn" aria-label="עזרה בפקודות קוליות" onClick={() => setVoiceHelp(true)}><CircleHelp size={21} /></button>
              <button className="icon-btn" aria-label={recorder.state !== "idle" ? "ביטול הקלטה" : "סגירת חיווי קולי"} onClick={() => { recorder.cancel(); recorder.clear(); handsFree.clear(); setVoicePanel(false); setVoiceMessage(""); }}><X size={20} /></button>
            </section>, document.querySelector(".app") || document.body
          )}
          {voiceHelp && createPortal(
            <section className="voice-panel" aria-label="פקודות קוליות ללא מגע">
              <header className="panel-toolbar">
                <span className="live-eyebrow">
                  <Mic size={18} /> פקודות קוליות
                </span>
                <button
                  className="question-close"
                  aria-label="סגירת עזרה קולית"
                  onClick={() => setVoiceHelp(false)}
                >
                  <X size={17} />
                </button>
              </header>
              <strong role="status">
                {handsFree.active
                  ? handsFree.state === "paused"
                    ? "ממתינים לסיום ההקראה…"
                    : handsFree.state === "requesting"
                      ? "ממתינים להרשאת המיקרופון…"
                    : handsFree.state === "transcribing"
                      ? "מזהים את מה שאמרת…"
                      : handsFree.state === "acting"
                        ? "מבצעים את הפקודה…"
                        : "מקשיבים לך, ללא מגע"
                  : recorder.state === "recording"
                    ? "מקשיבים לפקודה אחת…"
                    : recorder.state !== "idle"
                      ? "מזהים ומבצעים…"
                      : "האזנה כבויה"}
              </strong>
              <p>
                אמרו ״מצלמה״ או ״משטרה״ כדי לדווח. לניווט: ״ניווט״, כתובת, ואז
                ״אנטר״ אחרי הקראת היעד. ״הבא״ מחליף תוצאה ו״ביטול״ מבטל את
                החיפוש.
              </p>
              {voiceMessage && (
                <p className="voice-message" role="status">
                  {voiceMessage}
                </p>
              )}
              {voiceDestination && (
                <p className="voice-destination">
                  <MapPin size={15} /> {voiceDestination.name} ·{" "}
                  {voiceDestination.address}
                </p>
              )}
              {(handsFree.transcript || recorder.transcript) && (
                <p className="voice-transcript">
                  זוהה: ״{handsFree.transcript || recorder.transcript}״
                </p>
              )}
              {(handsFree.error || recorder.error) && (
                <p className="error-text" role="alert">
                  {handsFree.error || recorder.error}
                </p>
              )}
              <button
                className="primary-btn"
                onClick={handsFree.active ? stopHandsFree : startHandsFree}
              >
                {handsFree.active ? <Square size={17} /> : <Mic size={17} />}
                {handsFree.active ? "כיבוי ההאזנה" : "הפעלת האזנה רציפה"}
              </button>
              <button
                className="text-btn"
                disabled={
                  recorder.state === "transcribing" ||
                  recorder.state === "acting"
                }
                onClick={() =>
                  recorder.state === "recording"
                    ? recorder.stop()
                    : startVoice()
                }
              >
                {recorder.state === "recording"
                  ? "סיום וזיהוי"
                  : "הקלטת פקודה אחת"}
              </button>
              <small>
                האזנה כשהאפליקציה מוצגת בלבד. זיהוי המכשיר מועדף באפליקציה; שרת Desty משמש לגיבוי ובדפדפן ללא תמיכה.
                שתיקה אינה נשלחת. ההאזנה נעצרת בזמן ההקראה.
              </small>
              <button className="text-btn" onClick={() => {
                f5Speech.unlock();
                speakOwned("בדיקת קול. ההנחיות של דסטי יושמעו כאן.", "community", () => !document.hidden);
              }}>בדיקת שמע</button>
              {speechStatus && <p role="status">{speechStatus}</p>}
              <p className="voice-help">אם ההרשאה נדחתה, פתחו את הגדרות Desty במכשיר ואפשרו מיקרופון. בדקו גם את עוצמת המדיה ואת חיבור ה־Bluetooth.</p>
            </section>, document.querySelector(".app") || document.body
          )}
          {reportError && (
            <div className="connection-note">
              <span />
              {reportError}
              <button onClick={() => setReportVersion((v) => v + 1)}>
                רענון
              </button>
            </div>
          )}
          {!online ? (
            <div className="tile-error" role="status">ללא קליטה · מוצגים אזורים שנשמרו במכשיר</div>
          ) : tileError && (
            <div className="tile-error" role="status">
              חלק מאריחי המפה לא נטענו. בדקו את החיבור לרשת.
            </div>
          )}
        </main>
      </div>
      {toast && (
        <div className="toast" role="status">
          <span>
            <Check size={18} />
          </span>
          {toast}
          <button onClick={() => setToast("")} aria-label="סגירת הודעה">
            <X size={16} />
          </button>
        </div>
      )}
      {accountPanel && <Modal label="חשבון והיסטוריה" onClose={() => setAccountPanel(false)}>
        <AccountPanel model={{ ...account, clearHistory: clearOwnHistory }} onClose={() => setAccountPanel(false)} onNavigate={planHistoryDestination} />
      </Modal>}
      {originPicker && <Modal label="מאיפה יוצאים?" onClose={() => setOriginPicker(false)}>
        <OriginPicker center={position ?? origin} currentLabel={originLabel} busy={gpsBusy} error={originError}
          onCurrent={() => void locate(true)} onSelect={chooseOrigin}
          onMap={() => { setOriginPicker(false); setSection("explore"); setPickMode("origin"); setSheetExpanded(false); setSheetFull(false); }} />
      </Modal>}
      {transportOpen && <Modal label="סוג תחבורה" onClose={()=>setTransportOpen(false)}><TransportPicker value={settings.travelMode} onChange={changeTransport}/></Modal>}
      {hourlyTraffic && route && <Modal label="זמני נסיעה לפי שעה" onClose={() => setHourlyTraffic(false)}>
        <HourlyTraffic routeId={route.id} baselineSeconds={route.duration} />
      </Modal>}
      {routeOverview && route && <Modal label="מסלול · כל הפניות" onClose={() => setRouteOverview(false)}>
        {usesRoadTraffic(settings.travelMode) && <div className="nav-sheet-actions"><button onClick={() => setHourlyTraffic(true)}><Clock3 size={18}/>זמני נסיעה לפי שעה</button></div>}
        <details className="route-extra"><summary>חלופות ואפשרויות נסיעה</summary>
          <div className="route-options">{routes.map((r, i) => <button key={r.id} className={"route-option " + (routeIndex === i ? "selected" : "")} disabled={mode !== "idle"} onClick={() => setRouteIndex(i)}><span>{i === 0 ? "מומלץ" : `חלופה ${i}`}</span><strong>{formatDuration(r.duration)}</strong><small>{formatDistance(r.distance)}</small></button>)}</div>
          <p>{destination?.address}</p>
          {safetyNotice && <p>{safetyNotice}</p>}
          <p>הזמן הוא הערכה; עדכוני תנועה חיים זמינים רק כשיש מספיק מדידות מנהגים.</p>
          {mode === "idle" && <div className="nav-sheet-actions">
            <button onClick={openOriginPicker}><Flag size={18}/>שינוי מוצא</button>
            <button onClick={startSimulation}><Play size={18}/>הדמיית המסלול</button>
            <button disabled={!!destination && isGooglePlace(destination)} onClick={() => saveDestination("favorite")}><Star size={18}/>שמירת היעד</button>
          </div>}
          <div className="nav-sheet-actions"><button onClick={() => { setRouteOverview(false); discover("fuel"); }}><Fuel size={18}/>דלק בדרך</button><button onClick={() => { setRouteOverview(false); discover("food"); }}><Coffee size={18}/>אוכל וקפה</button></div>
        </details>
        <RouteOverview route={route} destination={destination?.name || "היעד שלך"} currentIndex={mode === "idle" ? undefined : progress?.stepIndex} />
      </Modal>}
      {suggestionPoint && <Modal label="הצעת מקום חדש" onClose={() => setSuggestionPoint(null)}><PlaceSuggestion point={suggestionPoint} onPickAgain={suggestPlace} onDone={() => setSuggestionPoint(null)} /></Modal>}
      {discovery && (
        <Modal
          label="מקומות בסביבה"
          onClose={() => setDiscovery(null)}
        >
          <Discovery
            center={discovery.center}
            centerLabel={discovery.centerLabel}
            initialCategory={discovery.category}
            onSelect={selectDiscovery}
            onPlacesChange={setNearbyPlaces}
            onSuggest={mode === "idle" ? suggestPlace : undefined}
          />
        </Modal>
      )}
      {showCatalog && (
        <Modal label="מצלמות דרך" onClose={() => setShowCatalog(false)}>
          <h2>מצלמות לאורך הדרך</h2>
          <CameraCatalogPanel
            catalog={cameraCatalog}
            error={catalogError}
            onRetry={() => setCatalogVersion((v) => v + 1)}
            onSelect={(camera) => {
              setShowCatalog(false);
              setActiveCamera(camera);
            }}
          />
        </Modal>
      )}
      {activeCamera && (
        <Modal label="פרטי מצלמה" onClose={() => setActiveCamera(null)}>
          <h2>{cameraLabel(activeCamera)}</h2>
          <p className="catalog-location">
            {activeCamera.description || activeCamera.name}
          </p>
          <dl className="catalog-details">
            <div>
              <dt>מצב מדווח</dt>
              <dd>{activeCamera.sourceStatus}</dd>
            </div>
            {activeCamera.speedLimitKph !== null && (
              <div>
                <dt>מהירות מדווחת</dt>
                <dd>{activeCamera.speedLimitKph} קמ״ש</dd>
              </div>
            )}
            {(activeCamera.heading !== null ||
              activeCamera.secondaryHeading !== null) && (
              <div>
                <dt>כיווני המצלמה</dt>
                <dd dir="ltr">
                  {[activeCamera.heading, activeCamera.secondaryHeading]
                    .filter((h) => h !== null)
                    .map((h) => `${h}°`)
                    .join(" / ")}
                </dd>
              </div>
            )}
          </dl>
          <p className="report-hint">
            פעילות המצלמה לא אומתה בזמן אמת. יש לנהוג לפי התמרור בדרך.
          </p>
          <button
            className="primary-btn full-width"
            onClick={() => {
              setShowCameraLayer(true);
              mapHandle.current?.locate(activeCamera);
              setActiveCamera(null);
            }}
          >
            <MapPin size={18} />
            הצגת המצלמה במפה
          </button>
          <button
            className="text-btn"
            onClick={() => {
              setActiveCamera(null);
              setShowCatalog(true);
            }}
          >
            לכל המצלמות
          </button>
        </Modal>
      )}
      {reportModal && (
        <Modal
          label="דיווח חדש"
          onClose={() => {
            if (cameraLookup.current) {
              cameraLookup.current.abort();
              cameraLookup.current = null;
              setReportBusy(false);
            }
            setReportModal(false);
          }}
        >
          <h2>
            {cameraRemoval
              ? "מצלמה כבר לא נמצאת"
              : reportType
                ? REPORTS[reportType].label
                : "מה קורה בדרך?"}
          </h2>
          {cameraRemoval && <p>בחרו את המצלמה שכבר אינה נמצאת. העדכון יסיר את הדיווח מהמפה של כולם.</p>}
          {cameraRemoval ? (
            <div className="camera-removal">
              {reportBusy ? (
                <div className="loading">
                  <LoaderCircle className="spin" size={18} /> בודקים דיווחי
                  מצלמות לידך…
                </div>
              ) : cameraRemovalError ? (
                <p role="alert" className="error-text">
                  {cameraRemovalError}
                </p>
              ) : cameraCandidates.length === 0 ? (
                <p>אין דיווחי מצלמות בטווח 500 מטר מהמיקום שלך.</p>
              ) : (
                cameraCandidates.map((camera) => (
                  <button
                    className="place-row"
                    key={camera.id}
                    disabled={reportBusy}
                    onClick={() =>
                      void resolveReport(camera)
                        .then(() => setReportModal(false))
                        .catch(() => {})
                    }
                  >
                    <span className="place-icon purple">
                      <CameraOff size={22} />
                    </span>
                    <span>
                      <strong>{REPORTS[camera.type].label}</strong>
                      <small>
                        {cameraRemovalOrigin
                          ? formatDistance(
                              distanceMeters(cameraRemovalOrigin, camera),
                            )
                          : ""}{" "}
                        ממך · {camera.note || "דיווח קהילה"}
                      </small>
                      <small>הסרת דיווח המצלמה</small>
                    </span>
                    <ChevronLeft size={18} />
                  </button>
                ))
              )}
              <p className="report-hint">
                נדרש GPS עדכני. מסירים רק מצלמה שבדקתם בשטח ושכבר אינה נמצאת.
              </p>
              <button
                className="outline-btn full-width"
                disabled={reportBusy}
                onClick={() => void openCameraRemoval()}
              >
                <RefreshCw size={17} /> בדיקה מחדש
              </button>
              <button
                className="text-btn back-report"
                disabled={reportBusy}
                onClick={() => setCameraRemoval(false)}
              >
                חזרה לסוגי הדיווח
              </button>
            </div>
          ) : !reportType ? (
            <>
              <div className="report-grid">
                {newReportTypes.map((type) => {
                  const { icon: Icon, label, color } = REPORTS[type];
                  return (
                    <button key={type} onClick={() => setReportType(type)}>
                      <span className={"place-icon " + color}>
                        <Icon size={27} />
                      </span>
                      <strong>{label}</strong>
                    </button>
                  );
                })}
              </div>
              <button
                className="camera-gone-action"
                onClick={() => void openCameraRemoval()}
              >
                <CameraOff size={22} />
                <span>
                  <strong>מצלמה כבר לא נמצאת</strong>
                  <small>עדכון דיווח קיים בסביבה שלך</small>
                </span>
                <ChevronLeft size={18} />
              </button>
            </>
          ) : (
            <>
              <div className="report-location">
                <MapPin size={20} />
                <div>
                  <strong>מיקום הדיווח</strong>
                  <small dir="ltr">
                    {reportPosition.lat.toFixed(5)},{" "}
                    {reportPosition.lng.toFixed(5)}
                  </small>
                  <small>
                    {position && distanceMeters(position, reportPosition) < 30
                      ? "המיקום שלך"
                      : "הנקודה שנבחרה במפה — ודאו שהיא נכונה"}
                  </small>
                </div>
                <button
                  className="text-btn"
                  onClick={() => {
                    setReportModal(false);
                    setPickMode("report");
                  }}
                >
                  שינוי
                </button>
              </div>
              <label className="field-label" htmlFor="report-note">
                משהו נוסף שכדאי לדעת? <span>לא חובה</span>
              </label>
              <textarea
                id="report-note"
                maxLength={240}
                rows={3}
                placeholder="למשל: בנתיב הימני, ליד הצומת…"
                value={reportNote}
                onChange={(e) => setReportNote(e.target.value)}
              />
              <div className="note-counter">{reportNote.length}/240</div>
              <p className="report-hint">
                מפרסמים רק אירוע שראיתם. הדיווח נשאר גלוי לקהילה עד לסימון
                שהאירוע הסתיים.
              </p>
              <button
                className="primary-btn full-width"
                disabled={reportBusy}
                onClick={submitReport}
              >
                {reportBusy ? (
                  <LoaderCircle className="spin" size={18} />
                ) : (
                  <Plus size={18} />
                )}
                פרסום דיווח
              </button>
              <button
                className="text-btn back-report"
                onClick={() => setReportType(null)}
              >
                חזרה לסוגי הדיווח
              </button>
            </>
          )}
        </Modal>
      )}
      {activeReport && (
        <Modal label="פרטי דיווח" onClose={() => setActiveReport(null)}>
          {(() => {
            const { icon: Icon, label, color } = REPORTS[activeReport.type];
            return (
              <>
                <span className={"place-icon large " + color}>
                  <Icon size={32} />
                </span>
                <h2>{label}</h2>
                <p>{activeReport.note || REPORTS[activeReport.type].detail}</p>
                <div className="report-metadata">
                  <span>
                    <Clock3 size={17} />
                    לפני{" "}
                    {Math.max(
                      1,
                      Math.floor((Date.now() - activeReport.createdAt) / 60000),
                    )}{" "}
                    דקות
                  </span>
                  <span>
                    <ShieldCheck size={17} />
                    {activeReport.confirmations} אישורים מהקהילה
                  </span>
                  <span>
                    <MapPin size={17} />
                    {formatDistance(distanceMeters(origin, activeReport))}{" "}
                    מנקודת המוצא
                  </span>
                </div>
                <p className="report-hint">
                  דיווח קהילה, ללא אימות רשמי. נשאר במפה עד שנהג שנמצא בקרבתו
                  מסמן שכבר אינו קיים.
                </p>
                <div className="report-confirm-buttons">
                  <button
                    className="primary-btn"
                    disabled={reportBusy}
                    onClick={() => void confirmReport(activeReport)}
                  >
                    <Check size={18} />
                    עדיין כאן
                  </button>
                  <button
                    className="outline-btn"
                    disabled={reportBusy}
                    onClick={() =>
                      void resolveReport(activeReport).catch(() => {})
                    }
                  >
                    <X size={18} />
                    {isCameraReport(activeReport.type)
                      ? "מצלמה כבר לא נמצאת"
                      : "כבר אין"}
                  </button>
                </div>
                {activeReport.mine && (
                  <button
                    className="outline-btn full-width"
                    onClick={() => deleteReport(activeReport)}
                    disabled={reportBusy}
                  >
                    <Trash2 size={18} />
                    הסרת הדיווח שלי
                  </button>
                )}
              </>
            );
          })()}
        </Modal>
      )}
      {about && (
        <Modal label="אודות Desty" onClose={() => setAbout(false)}>
          <span className="brand-mark about-mark">
            <Navigation size={30} fill="currentColor" />
          </span>
          <h2>אודות Desty</h2>
          <p>
            Desty מבית SKYTHEL היא אפליקציית ניווט פתוחה עם מקומות שמורים ודיווחי קהילה, שנבנתה
            כדי שיהיה פשוט להגיע.
          </p>
          <div className="about-features">
            <div>
              <Map size={20} />
              <span>
                מפות Desty לכל העולם, מאוחסנות ומוגשות מהשרתים שלנו.
                אזורים נטענים לפי התצוגה ונשמרים במכשיר במטמון מוגבל לצפייה ללא חיבור.
              </span>
            </div>
            <div>
              <Map size={20} />
              <span>
                מקור נתוני המפה: ©{" "}
                <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
                  OpenStreetMap contributors
                </a>{" "}
                ברישיון ODbL. הצגת המפה אינה פונה לשרתי OpenStreetMap.
                גם חיפוש הכתובות והמקומות פועל על מאגרים בשרתים שלנו.
              </span>
            </div>
            <div>
              <Search size={20} />
              <span>
                נתוני המקומות:{" "}
                <a
                  href="https://docs.overturemaps.org/attribution/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Overture Maps Foundation
                </a>{" "}
                וספקיה, ובהם Meta, Microsoft ו־Foursquare.{" "}
                <a
                  href="/data-licenses/CDLA-Permissive-2.0.txt"
                  target="_blank"
                  rel="noopener"
                >
                  CDLA
                </a>{" "}
                ·{" "}
                <a
                  href="/data-licenses/Apache-2.0.txt"
                  target="_blank"
                  rel="noopener"
                >
                  Apache
                </a>{" "}
                ·{" "}
                <a
                  href="/data-licenses/CC0-1.0.txt"
                  target="_blank"
                  rel="noopener"
                >
                  CC0
                </a>{" "}
                ·{" "}
                <a
                  href="/data-licenses/Foursquare-NOTICE.txt"
                  target="_blank"
                  rel="noopener"
                >
                  הודעות ייחוס
                </a>
              </span>
            </div>
            <div>
              <RouteIcon size={20} />
              <span>
                חישוב מסלולים בשרתי Desty. זמני הגעה מתעדכנים לפי
                מדידות נהגים כשיש מספיק מידע עדכני.
                ללא קליטה אפשר לצפות במפה שנשמרה ולחפש במקומות שכבר הורדו;
                חישוב מסלול חדש דורש חיבור לשרתי Desty.
              </span>
            </div>
            <div>
              <LocateFixed size={20} />
              <span>
                ניווט GPS בחזית, הקראה בעברית ופקודות קוליות לדיווח
                ולאימות אירועים.
              </span>
            </div>
            <div>
              <ShieldCheck size={20} />
              <span>
                דיווחי קהילה בזמן אמת, לצד מאגר מצלמות לאורך הדרך.
                נקודות המאגר מוצגות עם סוג ומצב לפי המקור; הפעילות בפועל אינה
                מאומתת בזמן אמת.
              </span>
            </div>
          </div>
          <p className="report-hint">
            צפייה ללא חיבור זמינה לאזורי מפה שנשמרו במכשיר ועדיין נמצאים במטמון.
            טעינת אזורים חדשים וחישוב מסלול חדש דורשים חיבור לשרתי Desty.
            חיפוש ללא קליטה מוגבל למקומות שכבר הורדו למכשיר.
          </p>
          <a
            className="text-btn"
            href="https://www.openstreetmap.org/fixthemap"
            target="_blank"
            rel="noreferrer"
          >
            דיווח על טעות בנתוני המפה
            <ArrowUpRight size={15} />
          </a>
        </Modal>
      )}
    </div>
  );
}
export default App;
